/**
 * Catalog API client — typed fetchers for the public catalog endpoints.
 *
 * The list endpoint returns { data: TenderRow[], meta: { pagination: {...} } }.
 * The detail endpoint returns { data: TenderDetail }.
 */
import { apiRequest, apiRaw, apiBaseUrl } from './api';
import { saveBlob } from './save-blob';
import { displaySourceText as sourceText } from './source-text';
import { countryConfig } from '../country';

/** Тип закупки — зеркалит ProcurementKind на бэкенде. */
export type ProcurementKind = 'goods' | 'works' | 'services';

/**
 * Narrow a raw `kind` URL param to a real {@link ProcurementKind}, or
 * `undefined` for anything else (missing, empty, garbage, a stale/truncated
 * link, a bot-appended value). This MUST be a guard, not a cast: `kind` comes
 * straight off a user-editable URL, and the API's `@IsIn(PROCUREMENT_KINDS)`
 * validation 400s on anything outside the three literals — an unchecked
 * `as ProcurementKind` would let that value reach `fetchCatalog`/
 * `fetchSemanticSearch`/the export, turning "no filter" into a hard failure
 * (the whole catalog list gets replaced by the error card) instead of
 * degrading gracefully. `KindTabs` only ever writes one of these three values
 * or deletes the param, so this is a no-op on the happy path.
 */
export function parseKindParam(value: string | null): ProcurementKind | undefined {
  return value === 'goods' || value === 'works' || value === 'services' ? value : undefined;
}

/**
 * The statuses the API's `@IsIn(STATUS_FILTER_VALUES)` accepts. Anything else in
 * the URL is "no filter", never passed through.
 */
const STATUS_PARAM_VALUES = [
  'discovered',
  'active',
  'closing',
  'closed',
  'expired',
  'archived',
  'all',
] as const;

/**
 * Guards for the query params that reach the catalog API untyped.
 *
 * An unrecognised value must degrade to "no filter" rather than travel onward:
 * the API validates with class-validator, so a bad one returns 400, and the
 * catalog page renders the whole-page error card whose Retry re-issues the same
 * 400 forever. It also 400s the category, kind and ifi-source facet counts in the
 * same render, because they share these params. Reachable from a bot-mangled
 * link, a stale bookmark or a hand-edited URL — `?status=open` is enough.
 *
 * `parseKindParam` and `parseIfiSourceParam` already did this; these are the six
 * that were left reading straight off `searchParams`.
 */
export function parseStatusParam(value: string | null): string | undefined {
  return value !== null && (STATUS_PARAM_VALUES as readonly string[]).includes(value)
    ? value
    : undefined;
}

/** A finite, non-negative integer, or nothing. `Number('abc')` is NaN, and NaN
 *  serialized into the query string as the literal "NaN" — which the API's
 *  `@IsInt` rejects. */
export function parseIntParam(value: string | null): string | undefined {
  if (value === null || value.trim() === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? String(Math.trunc(n)) : undefined;
}

/** An ISO-8601 date the API's `@IsISO8601` will accept, or nothing. */
export function parseIsoDateParam(value: string | null): string | undefined {
  if (value === null || value.trim() === '') return undefined;
  // Shape first — rejects "yesterday", "01/02/2026" and the rest.
  //
  // The TIME components are range-checked in the pattern, not just shape-checked.
  // An earlier version accepted `\d{2}:\d{2}`, so `2026-01-01T99:99` passed the
  // guard and then 400'd on the API's `@IsISO8601` — reproducing the whole-page
  // error card this function exists to prevent. Hours are 00–23 (24:00 is legal
  // ISO-8601 but validator.js rejects it, and matching the API is the point),
  // minutes and seconds 00–59, and the UTC offset is bounded the same way.
  const m =
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ]([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(?:\.\d+)?)?(?:Z|[+-](?:[01]\d|2[0-3]):?[0-5]\d)?)?$/.exec(
      value,
    );
  if (!m) return undefined;

  // Then a real calendar date. `Date.parse` is not enough on its own: it accepts
  // '2026-02-31' and silently rolls it over to March 3, so the catalog would be
  // filtered by a date nobody asked for. Round-trip the y/m/d instead.
  const [, y, mo, d] = m;
  const dt = new Date(`${y}-${mo}-${d}T00:00:00Z`);
  if (Number.isNaN(dt.getTime())) return undefined;
  const roundTrips =
    dt.getUTCFullYear() === Number(y) &&
    dt.getUTCMonth() + 1 === Number(mo) &&
    dt.getUTCDate() === Number(d);
  return roundTrips ? value : undefined;
}

/** Институты слоя `ifi` — та же пятёрка, что и IFI_SOURCE_KEYS на сервере (#53). */
export const IFI_SOURCE_KEYS = [
  'worldbank',
  'adb',
  'aiib',
  'isdb',
  'ebrd',
  'uzrd',
  'undp',
  'ungm',
  'osce',
] as const;
export type IfiSourceKey = (typeof IFI_SOURCE_KEYS)[number];

/** Как и parseKindParam: неизвестное значение из URL — это «фильтра нет», а не
 *  400 от сервера, который заменил бы весь список карточкой ошибки. */
export function parseIfiSourceParam(value: string | null): IfiSourceKey | undefined {
  return IFI_SOURCE_KEYS.includes(value as IfiSourceKey) ? (value as IfiSourceKey) : undefined;
}

/** Счётчики по институтам (см. GET /catalog/ifi/source-counts). `all` считает
 *  весь слой, включая источники без чипа, поэтому сумма пяти может быть меньше. */
export type IfiSourceCounts = Record<IfiSourceKey, number> & { all: number };

export interface KindCounts {
  goods: number;
  works: number;
  services: number;
  /** Все тендеры под текущими фильтрами, включая тендеры без типа. */
  all: number;
}

/** A winning company's own contact, from the org registry by ИНН. At least
 *  one field is non-null whenever the object exists. Paid-only on every surface
 *  that carries it (tender outcome, supplier header, buyer recent contracts) —
 *  the API omits it for everyone else, and from every LIST payload for everyone
 *  including Pro (anti-harvest), so no catalogue/results row ever renders it. */
export interface WinnerContact {
  phone: string | null;
  email: string | null;
  // Yandex-Maps enrichment (2026-10-07): only on the lot outcome block and the
  // supplier Pro header, and only when present — absent (never null) otherwise.
  /** Business category from the map card, one line («Туризм · Гостиницы»), Russian. */
  category?: string;
  /** Absolute `http(s)://` URL. */
  website?: string;
  /** `t.me/<handle>` or `t.me/+<phone>`, no scheme. */
  telegram?: string;
  /** The map card's address (not the registry's legal one). */
  mapAddress?: string;
}

export interface CatalogItem {
  id: string;
  title: string;
  /** Spain (`content.castilianTitles`): the title the platform published, present only when
   *  it is Catalan, Valencian, Galician or Basque and `title` is its Castilian translation.
   *  Rendered as «Título original: …» (`OriginalTitle`). Absent on every other lot. */
  originalTitle?: string;
  /** Locale-neutral URL slug from the API for `/tender/{slug}-{id}`, or null. */
  slug: string | null;
  /** AI summary text (UZ/RU), or '' when none. */
  description: string;
  /** Richer source detail (full subject + line items), or '' when none. */
  detail: string;
  /** True when `detail` fell back to the untranslated source text because the
   *  ru/uz variant for the current locale hasn't been backfilled yet. Drives
   *  the "(source language)" disclaimer note. */
  descriptionIsSourceFallback?: boolean;
  /** Extracted ТЗ/smeta document text — gated, '' unless the viewer is entitled. */
  documentText: string;
  /** AI-extracted structured procurement requirements — gated exactly like
   *  `documentText` (derived from the same paid doc text), null unless the
   *  viewer is entitled. Detail view only. */
  requirements?: TenderRequirements | null;
  /** True when requirements exist but this viewer isn't entitled — drives an
   *  upgrade CTA instead of rendering nothing. Detail view only. */
  requirementsLocked?: boolean;
  /** Non-sensitive existence flag: true when the API's `requirements` object is
   *  non-null for this lot — our own extraction (any field filled), OR the
   *  marketplace criteria list the API merges in since 2026-09-25
   *  (`requirementsState === 'marketplace'`). Present on the list AND the detail.
   *  ⚠️ Nothing renders off it today: the qualification block keys off
   *  `requirements` + `requirementsState`, the gate off `hasContact` /
   *  `hasDocuments`. A hide-when-empty rule wired to it would now be safe — a
   *  marketplace-only lot reads TRUE — but it is NOT "has a criteria list": a
   *  `none_stated` lot reads TRUE with no list. */
  hasRequirements?: boolean;
  /** WHY the qualification block shows what it shows — see `RequirementsState`.
   *  Absent when the API predates it (2026-09-25); the panel then renders
   *  exactly as before rather than on an `undefined`. Detail view only. */
  requirementsState?: RequirementsState | null;
  /** Provenance of the rendered criteria: our document extraction, or the
   *  marketplace's own published criteria. Null when there is no list. */
  requirementsSource?: RequirementsSource | null;
  /** When `requirementsSource === 'marketplace'`: the subset of
   *  `requirements.qualifications` the source marks as disqualifying. */
  requirementsDisqualifying?: string[] | null;
  /** «Criterios de adjudicación» — a PLACSP (Spain) lot's scored award criteria, already
   *  taken out of `requirements.qualifications` by the API. Absent on every UZ/KZ lot. */
  awardCriteria?: AwardCriterion[];
  /** Human name of the originating marketplace, for the «по данным площадки
   *  {name}» copy. Ungated — a portal's display name, not the Pro-gated
   *  `(sourceKey, externalId)` locator pair. */
  requirementsSourceName?: string | null;
  /** «Площадка · № лота» on the card: the origin platform's name and its own
   *  lot number. Pro-only — the API omits both keys for anyone else. */
  sourceName?: string | null;
  lotNumber?: string | null;
  /** True when the tender has documents behind the paid wall — drives the
   *  "documents locked" teaser for non-entitled viewers. */
  hasDocuments?: boolean;
  /** The locked file list for a non-entitled viewer — names/types only, no
   *  URLs (2026-09-06). Null/absent for an entitled viewer, who gets
   *  `documents`. Detail view only. */
  documentsPreview?: { name: string; type?: string }[] | null;
  /** The lot's document text was transcribed from a SCAN by the model (API
   *  `textFromScan`, i.e. `document_text_source = 'ocr'`), so the summary and
   *  requirements built from it carry «Распознано со скана…». Public, detail
   *  view only; only ever `true` or absent. */
  textFromScan?: true;
  /** Non-sensitive existence flag (never the contact itself): true when the
   *  tender carries a заказчик contact behind the wall. Drives the "no empty
   *  paywall" rule on the detail page — a tender with neither a contact nor
   *  documents renders no Pro wall and no Pro CTA, because unlocking it would
   *  reveal nothing. Detail view only. */
  hasContact?: boolean;
  /** Region display name (e.g. "Samarkand"). */
  region: string;
  /** Region code used as the catalog filter value + /region/:slug param (e.g. "SAM"). */
  regionCode: string;
  /** Primary category display name (e.g. "Construction & Infrastructure"). */
  category: string;
  /** Primary category slug used as the filter value + /category/:slug param. */
  categorySlug: string;
  /** All categories on the tender, for tag pills. */
  categories: Array<{ slug: string; name: string }>;
  status: string;
  /** Budget in UZS as a raw integer string, or null when not disclosed. */
  budgetUzs: string | null;
  /** MiLicita: the whole expediente's budget (no VAT) of a PLACSP lot that has
   *  no budget of its own — shown as «Importe total del expediente» where the
   *  card would say «No indicado». Absent everywhere else. */
  expedienteBudgetUzs?: string;
  /** Bid security / обeспечение заявки, as stored — EITHER an absolute so'm
   *  amount OR a percent of the budget, depending on the source. Never render
   *  it raw; see `formatBidSecurity` in `lib/format.ts`. */
  bidSecurityUzs: string | null;
  /** The deposit RATE the source published («3» = 3 %), or null for sources
   *  that publish so'm. Pass it to `formatBidSecurity` as `publishedPct`:
   *  since 2026-09-29 `bidSecurityUzs` holds the so'm the rate works out to.
   *  Optional only so hand-built items (tests, fixtures) need not name it —
   *  `rowToItem` always sets it. */
  bidSecurityPct?: number | null;
  publishedAt: string;
  deadline?: string;
  /** A live «анонс»: the procedure is announced and planned, bids are not open
   *  yet, so there is no deadline — the card and the lot page show «Скоро приём
   *  заявок» in its place. Set by the API from `procedure.announcement`, on the
   *  list AND the detail; absent otherwise. */
  announcement?: true;
  /** MiLicita: a «consulta preliminar del mercado» (PLACSP feed 1403) — the buyer asks
   *  the market before drafting a tender. A live lot with a deadline (the last day to
   *  answer); the card and the lot page name the stage. Set by the API, list AND detail;
   *  absent otherwise and on every UZ/KZ lot. */
  consultation?: true;
  /** Source adapter key, for the public deep-link to the original tender. */
  sourceKey?: string | null;
  /** Namespaced source id, for the deep-link. */
  externalId?: string | null;
  /** Verbatim per-lot source URL, captured at harvest for sources (e.g. ebirja)
   *  whose deep link can't be derived from sourceKey+externalId. Same Pro-only
   *  gate as that pair. */
  sourceUrl?: string | null;
  /** Public buyer (заказчик) org name, resolved from the tender's customer_tin,
   *  or null when unresolved. Shown on the card for scannability — disambiguates
   *  lots that share a bare, generic title (goszakup/KZ: «Книга» ×109…). */
  buyerName?: string | null;
  /** Public buyer ИНН, for the card/detail `/buyer/{tin}` link. Present on the
   *  list and the single-tender detail alike (mirrors `buyerName`); null for
   *  IFI lots with no buyer profile. */
  buyerTin?: string | null;
  /** Каталожный слой тендера. Карточка меняет вид при 'ifi'. Required so a
   *  construction site can't silently omit it and render an IFI lot as a
   *  government card (mirrors `TenderView.scope` on the API side). */
  scope: string;
  /** Тип нотиса института (ifb | reoi | gpn | advance | firm). Только для ifi. */
  noticeType: string | null;
  /** Harvested tender documentation (download links), detail view only. */
  documents?: TenderDocument[] | null;
  /** Harvested procedure metadata (method, financing, dates…), detail view only. */
  procedure?: Record<string, unknown> | null;
  /** Public per-lot structured detail (unit price, qty, unit, delivery place/term,
   *  Incoterms, advance %…), detail view only. Display-only, ungated. */
  lotDetails?: Record<string, unknown> | null;
  /** «Что закупают» — the lot's line items, public to every viewer. Detail view:
   *  the full (capped) list. /results rows (the awarded list): a preview of the
   *  first few names and quantities, no prices (`PositionsPreview` in
   *  tender-list.tsx). Absent when the lot has none, on every other list, or
   *  when the API predates the field. */
  positions?: TenderPosition[];
  /** The source's own position count when it exceeds `positions.length`. */
  positionsTotal?: number;
  /** True when the public detail returns only a teaser and the full AI summary
   *  is gated behind Pro (detail view only). */
  summaryLocked?: boolean;
  /** Award outcome — winner + final price — once the lot is awarded, else null.
   *  PAID since 2026-10-07: the API sends the three to a paid viewer only; for a
   *  guest or a free account they are null here and `outcomeLocked` is set. */
  winnerName?: string | null;
  winnerInn?: string | null;
  /** The lot HAS a winner this viewer may not see (outcome wall, 2026-10-07).
   *  Drives the wall on the detail page and the lock line on /results, and
   *  keeps a guest-rendered awarded page indexable (`isTenderIndexable`).
   *  ABSENT unless true — never materialised as `false` (see `rowToItem`). */
  outcomeLocked?: true;
  /** Beside `outcomeLocked` only: whether a final price unlike the budget, and
   *  a winner phone/email, exist behind the wall — they pick the wall's
   *  sentence (`outcomeLockText`). Absent from an API one deploy behind. */
  outcomeHasPrice?: boolean;
  outcomeHasContact?: boolean;
  /** The winner was COPIED from tender_results, not harvested (2026-10-09) —
   *  the page is `noindex` (`isTenderIndexable`). ABSENT unless true. */
  outcomeTransferred?: true;
  /** The WINNER's own phone/email (from the org registry, by `winnerInn`).
   *  PAID: the API sends it only to a full-access viewer and only when the
   *  registry holds one; for everyone else — and in every prerendered payload —
   *  the key is ABSENT. Never materialise it as `null` (see `rowToItem`). */
  winnerContact?: WinnerContact;
  /** True when a paid viewer has used up today's ceiling of distinct lots with
   *  a winner contact (200 per Tashkent day, server-side): `winnerContact` is
   *  absent and the page says why instead of the line silently vanishing. */
  winnerContactCapped?: boolean;
  finalPriceUzs?: string | null;
  awardedAt?: string | null;
  /** Lot outcome as the SOURCE published it: `'awarded'` | `'failed'` («Закупка
   *  не состоялась»), or null/absent when not known yet. A `'failed'` lot has
   *  no `winnerName` to distinguish it from an ordinary expired lot — render
   *  this explicitly rather than infer failure from a missing winner. Public,
   *  never Pro-gated. */
  outcomeStatus?: string | null;
  /** WHY a `'failed'` lot has no winner: `'cancelled'` (the procedure was
   *  cancelled), `'no_bids'` (nobody bid), `'failed'` (no winner determined).
   *  Absent otherwise — the API maps xt-xarid's stored values to `'failed'` +
   *  this, so every `outcomeStatus === 'failed'` test keeps working. */
  outcomeReason?: 'cancelled' | 'no_bids' | 'failed';
  /** Other lots of the SAME procurement announcement/batch (KZ goszakup/
   *  mitwork/skpharmacy routinely split one purchase into dozens of single-
   *  product lots). Detail view only; absent when there are fewer than two
   *  siblings. See `SiblingLotItem`. */
  siblingLots?: SiblingLotItem[];
  /** Real sibling count — may exceed `siblingLots.length` when the list was
   *  capped server-side. */
  siblingLotsTotal?: number;
  /** Sum of the SIBLING lots' budgets (so'm/tenge) as a raw integer string, or
   *  null when none of them disclose a budget. Excludes this lot's own budget. */
  siblingLotsTotalBudgetUzs?: string | null;
  /** When the tender's substantive content last changed (ISO). Drives the tender
   *  JSON-LD dateModified. Detail view only; not rendered directly. */
  contentChangedAt?: string | null;
  /** True when the server has decided THIS viewer may see the paid parts —
   *  Pro-only now (pure subscription gate). Detail view only; defaults to false
   *  on the list view / when absent. */
  unlocked?: boolean;
  /** Free-plan monthly unlock state for THIS tender/viewer — null when the
   *  viewer has no free-unlock entitlement at all (Pro, or anonymous).
   *  `eligible` gates whether the unlock button may be shown, `remaining` is
   *  opens left this month, `unlocked` is true once already spent on this
   *  tender (re-viewing is free), and `missing` lists profile fields
   *  (`tin`/`phone`/a taken `phone`) that block eligibility until fixed.
   *  Detail view only. */
  freeUnlock?: {
    eligible: boolean;
    remaining: number;
    unlocked: boolean;
    missing: Array<'tin' | 'phone' | 'phone_taken'>;
  } | null;
  /** The заказчик contact, delivered INLINE (decrypted) for a Pro viewer — null
   *  for non-Pro. Detail view only; there is no separate "open" request. */
  contact?: { name: string | null; email: string | null; phone: string | null } | null;
  /** Per-viewer contact access; see ContactAccess. */
  contactAccess?: ContactAccess;
  /** Detail-only: may THIS viewer build a «Пакет заявки» for this lot? True
   *  only when the viewer holds the `bid_package` feature grant. Absent (not
   *  `false`) otherwise, so without the grant the payload is exactly what it was
   *  before the feature existed. Like `unlocked`, this is the server's answer —
   *  never re-derive it from auth-context. */
  bidPackage?: boolean;
  /**
   * Saved-tenders only: a package for this lot is BUILT, and where to get it.
   *
   * ⚠️ A different fact from `bidPackage` above, which is «this viewer may build
   * one». Both arrive on the wire under the one key the v4 contract names
   * (`bidPackage: { ready, downloadUrl }` on a favourites row, `bidPackage: true`
   * on the detail) and are split here on purpose: one is a capability and the
   * other is a finished artefact, `bidPackage && …` already gates a whole panel
   * on the first, and a union would make every reader of either decide which it
   * had. Absent when nothing is built — `ready: false` never reaches here.
   *
   * `downloadUrl` is absolute by the time it is on an item; see `rowToItem`.
   */
  bidPackageReady?: { downloadUrl: string };
  /** Detail-only: soliq registry trust facts for the buyer org resolved from
   *  the tender's customer TIN, or null when the tender has no customer TIN or
   *  no matching organizations row. Public data (the org's own registry
   *  state), same posture as the public buyer-profile trust strip.
   *  `charterFundUzs` is a string — BigInt has no native JSON representation.
   *  `regDate`/`liquidationDate` are ISO `yyyy-MM-dd`. */
  buyerTrust?: {
    name: string | null;
    status: string | null;
    statusGroup: string | null;
    vatPayer: boolean | null;
    charterFundUzs: string | null;
    regDate: string | null;
    liquidationDate: string | null;
    /** КГД register hits (KZ only) — rendered as warnings by `BuyerTrustStrip`. */
    kgdFlags?: string[];
  } | null;
}

export interface TenderDocument {
  name: string;
  url: string;
  type?: string;
}

/** One line item of «Что закупают» (`TenderView.positions` on the API) —
 *  normalized from the lot's stored source detail. Optional keys are absent
 *  when the source gave nothing; money is whole so'm. */
export interface TenderPosition {
  name: string | null;
  qty?: number;
  unit?: string;
  unitPriceUzs?: number;
  totalUzs?: number;
  code?: string;
}

/** One row in `CatalogItem.siblingLots` — another lot of the same procurement
 *  announcement/batch. Thin on purpose: the full lot is one click away. */
export interface SiblingLotItem {
  id: string;
  title: string;
  slug: string | null;
  budgetUzs: string | null;
  status: string;
}

/** AI-extracted structured procurement requirements — resolve-only, every
 *  field is null when the source text doesn't state it (never invented). */
/** Mirrors `services/api/src/modules/catalog/requirements-state.ts`. */
export type RequirementsState =
  | 'documents'
  | 'marketplace'
  | 'none_stated'
  | 'no_documents'
  | 'unreadable'
  | 'pending';

export type RequirementsSource = 'documents' | 'marketplace';

/** Mirrors `AwardCriterionView` in `services/api/src/modules/catalog/award-criteria.ts`. */
export interface AwardCriterion {
  name: string;
  /** Points as the buyer published them; null when unweighted. */
  weight: number | null;
  /** `formula` = evaluable by formula, `judgement` = juicio de valor. */
  method: 'formula' | 'judgement' | null;
}

export interface TenderRequirements {
  scope: string | null;
  qualifications: string[] | null;
  technicalSpecs: string[] | null;
  delivery: string | null;
  warranty: string | null;
  payment: string | null;
}

export interface CatalogListResult {
  items: CatalogItem[];
  total: number;
  offset: number;
  limit: number;
  /** True when the requesting viewer is Pro — envelope-level (describes the
   *  requester, not any one row), resolved server-side via the same canonical
   *  entitlement check the tender-detail page's `unlocked` uses (GET /catalog
   *  and GET /catalog/search both set meta.viewerIsPro). The list UI uses this
   *  alone to decide whether to render `ifi`-layer cards live or as a locked
   *  teaser — never re-derive Pro state from auth-context here. */
  viewerIsPro: boolean;
  /** False when `total` is only a LOWER BOUND — "at least this many". Set by
   *  GET /catalog/search, whose semantic (k-NN) arm is bounded by construction:
   *  once it comes back full there is no honest count of what lies beyond it.
   *  Every plain-list path counts with COUNT(*) and is always exact. The UI must
   *  render «не менее N» rather than a precise number when this is false —
   *  reporting a truncated count as a real one is the defect this exists to
   *  stop. */
  totalIsExact: boolean;
  /** Niches whose NAME or keyword the query matched, when the keyword itself
   *  came back thin. The top zero-result searches on this site are its own
   *  category names — «клининг» has zero matches in any tender text while the
   *  Клининг niche holds 25 live lots. Absent unless the API sent hints, so
   *  every consumer reads it as `?? []` and no fixture has to carry it. */
  nicheHints?: Array<{ slug: string; name: string }>;
  /** Slug of the one niche whose lots were APPENDED to `items` (list path only,
   *  and only when exactly one matched). When set the UI MUST say so: those
   *  rows are not what the user typed. */
  nicheApplied?: string;
  /** When the query is a bare 9-digit ИНН/СТИР, the org profile(s) it resolves
   *  to in the award graph — a paste of an org number should offer a link to the
   *  buyer/supplier profile, not an empty page. Absent unless the API sent them,
   *  so every consumer reads it as `?? []`. */
  orgHints?: Array<{ tin: string; kind: 'buyer' | 'supplier'; name: string | null }>;
  /** The query's digits when the API answered it as a LOT NUMBER (an exact
   *  `lot_number`/`external_id` hit) — the UI says «Найден лот № …». Absent on
   *  every word search. */
  lotNumberMatch?: string;
  /** Spain only: the query was CPV codes and the items are every lot under
   *  their subtrees (API `cpv-code-query.ts`) — the UI says «CPV … y sus
   *  subcódigos». Absent on every word search and on UZ/KZ. */
  cpvCodeMatch?: string[];
  /** «Возможно, вы имели в виду …» — `/catalog/search` only, first page, when the
   *  typed words matched no lot lexically. A suggestion; the items are unchanged. */
  didYouMean?: string;
}

export interface CatalogFilters {
  /**
   * Words that must NOT appear, comma-separated — the catalog's negative axis.
   *
   * Compiled server-side by the same `excludeText` the saved-search alert sweep
   * uses, so «исключить слово» means one thing across browsing, the digest and
   * the preview count.
   */
  exclude?: string;
  region?: string;
  /** Buyer (заказчик) ИНН — exact match on Tender.customerTin (buyer
   *  subscription axis). Only honoured by the plain list (`buildCatalogQuery`
   *  / GET /catalog); the search endpoint (`buildSearchQuery` / GET
   *  /catalog/search) does not accept it yet, so a keyword query silently
   *  drops this axis — same documented limitation as `sort` and the budget/
   *  deadline bounds while searching. */
  customer?: string;
  category?: string;
  /** ИКПУ classification code (hierarchical — parent matches sub-codes). */
  ikpu?: string;
  /** Niche slug (Spec #3 classification layer) — expands server-side to its
   *  underlying ИКПУ code set. Mutually exclusive with `category`/`ikpu` on
   *  the same single niche axis; the API applies `niche` when both are sent. */
  niche?: string;
  /** Тип закупки. Ось, независимая от `category`/`ikpu`/`niche`. */
  kind?: ProcurementKind;
  status?: string;
  /** Каталожный слой: 'ifi' | 'corporate' | 'gov' | 'all'.
   *  НЕ передавать по умолчанию — без параметра API сам применяет
   *  свой дефолт (gov + corporate). */
  scope?: string;
  /** Институт внутри слоя `ifi` (#53). Вне scope='ifi' сервер его игнорирует. */
  ifiSource?: IfiSourceKey;
  q?: string;
  /** Sort key, e.g. 'deadline', '-budgetUzs', '-createdAt'. */
  sort?: string;
  /** Budget range (so'm). */
  minBudget?: number;
  maxBudget?: number;
  /** Deadline range (ISO yyyy-mm-dd). */
  deadlineFrom?: string;
  deadlineTo?: string;
  /** ISO-8601 lower bound on first-seen time — "new since" for digest links. */
  since?: string;
  /** Restrict to lots that carry an award outcome (winner + final price).
   *  Paired with `status: 'closed'` this is the OUTCOMES set — the ~4k finished
   *  lots that `isTenderIndexable` lets into the index, and the only finished
   *  ones the sitemap advertises. Everything else that is finished is noindex,
   *  so never build a browsable listing out of it. */
  awarded?: boolean;
  /** Only outcomes that landed within the last N days (award date, else the
   *  deadline) — /results sends `RESULTS_WINDOW_DAYS` (30): the page is the
   *  last month of winners (owner, 2026-10-07). */
  outcomeDays?: number;
  /** The /results card's whole state as one `SavedSearchCriteria` JSON
   *  (`lib/results-search.ts`). Plain list only (GET /catalog) — the card
   *  never sends `q`, so it never takes the search endpoint. */
  criteria?: string;
  /** A saved search's whole definition as one `alert=` JSON — the link a
   *  search opens the catalogue with on Spain (`features.alertCriteriaLink`,
   *  `savedSearchToCatalogPath`). Plain list only (GET /catalog). */
  alert?: string;
  offset?: number;
  limit?: number;
  lang?: string;
}

export interface RegionItem {
  /** Region code used as the catalog `region` filter value (e.g. "SAM"). */
  slug: string;
  name: string;
  count: number;
}

export interface CategoryItem {
  /** Category slug used as the catalog `category` filter value. */
  slug: string;
  name: string;
  count: number;
}

/** Raw API row returned by GET /api/catalog (and other endpoints that reuse the
 *  TenderView shape, e.g. /me/saved-tenders). Exported so those clients can map
 *  rows through {@link rowToItem}. */
export interface CatalogRow {
  id: string;
  title: string;
  /** Spain (`content.castilianTitles`): the platform's own title of a lot whose `title` is its
   *  Castilian translation. Absent on every other lot. */
  originalTitle?: string | null;
  slug?: string | null;
  status: string;
  budgetUzs?: string | null;
  expedienteBudgetUzs?: string;
  bidSecurityUzs?: string | null;
  /** Absent from an API build older than 2026-09-29 — read as null. */
  bidSecurityPct?: number | null;
  deadline?: string;
  announcement?: boolean | null;
  consultation?: boolean | null;
  region?: { code: string; name: string } | null;
  categories?: Array<{ slug: string; name: string }>;
  summary?: string | null;
  description?: string | null;
  descriptionIsSourceFallback?: boolean | null;
  documentText?: string | null;
  requirements?: TenderRequirements | null;
  requirementsLocked?: boolean | null;
  hasRequirements?: boolean | null;
  requirementsState?: RequirementsState | null;
  requirementsSource?: RequirementsSource | null;
  requirementsDisqualifying?: string[] | null;
  awardCriteria?: AwardCriterion[] | null;
  requirementsSourceName?: string | null;
  sourceName?: string | null;
  lotNumber?: string | null;
  hasDocuments?: boolean | null;
  documentsPreview?: { name: string; type?: string }[] | null;
  textFromScan?: boolean | null;
  hasContact?: boolean | null;
  sourceKey?: string | null;
  externalId?: string | null;
  sourceUrl?: string | null;
  buyerName?: string | null;
  /** Public buyer ИНН for the `/buyer/{tin}` link. Present on the list and the
   *  single-tender detail alike (mirrors `buyerName`); null for IFI lots with
   *  no buyer profile. */
  buyerTin?: string | null;
  scope?: string | null;
  noticeType?: string | null;
  documents?: TenderDocument[] | null;
  procedure?: Record<string, unknown> | null;
  lotDetails?: Record<string, unknown> | null;
  positions?: TenderPosition[] | null;
  positionsTotal?: number | null;
  summaryLocked?: boolean | null;
  winnerName?: string | null;
  winnerInn?: string | null;
  outcomeLocked?: boolean | null;
  outcomeHasPrice?: boolean | null;
  outcomeHasContact?: boolean | null;
  outcomeTransferred?: boolean | null;
  winnerContact?: WinnerContact;
  winnerContactCapped?: boolean;
  finalPriceUzs?: string | null;
  awardedAt?: string | null;
  outcomeStatus?: string | null;
  outcomeReason?: 'cancelled' | 'no_bids' | 'failed' | null;
  siblingLots?: SiblingLotItem[] | null;
  siblingLotsTotal?: number | null;
  siblingLotsTotalBudgetUzs?: string | null;
  contentChangedAt?: string | null;
  /** Detail-only: true when THIS viewer may see the paid parts — Pro-only.
   *  Absent on the list view. */
  unlocked?: boolean | null;
  /** Detail-only: free-plan monthly unlock state for this viewer/tender. See
   *  {@link CatalogItem.freeUnlock}. */
  freeUnlock?: {
    eligible: boolean;
    remaining: number;
    unlocked: boolean;
    missing: Array<'tin' | 'phone' | 'phone_taken'>;
  } | null;
  /** Detail-only: the inline decrypted contact for a Pro viewer, else null. */
  contact?: { name: string | null; email: string | null; phone: string | null } | null;
  /** Detail-only, per-viewer. See ContactAccess — `unlocked` alone cannot
   *  describe a metered plan, which is how the ИП tier ended up showing its
   *  paying subscribers the free user's wall. */
  contactAccess?: ContactAccess;
  /**
   * ONE wire key, two facts — see `CatalogItem.bidPackage` /
   * `CatalogItem.bidPackageReady`.
   *
   * `true` on a tender detail = «this viewer may build one». The object form, on
   * a `/me/saved-tenders` row = «one is built, here it is» (v4 status contract,
   * `docs/explanation/bid-package-v4-plan.md`). Never both shapes on one row.
   */
  bidPackage?: boolean | { ready: boolean; downloadUrl: string } | null;
  /** Detail-only: soliq registry trust facts for the buyer org. See
   *  `CatalogItem.buyerTrust`. */
  buyerTrust?: {
    name: string | null;
    status: string | null;
    statusGroup: string | null;
    vatPayer: boolean | null;
    charterFundUzs: string | null;
    regDate: string | null;
    liquidationDate: string | null;
    /** КГД register hits (KZ only) — rendered as warnings by `BuyerTrustStrip`. */
    kgdFlags?: string[];
  } | null;
}

/** What the signed-in viewer may do about this tender's contact. Absent for
 *  anonymous callers, so prerendered HTML never varies on it. */
export interface ContactAccess {
  /** `full` — the contact is already inline. `metered` — a button that spends
   *  one of the plan's monthly opens. `none` — the paywall. */
  mode: 'full' | 'metered' | 'none';
  /** Opens left this month; null unless `metered`. */
  remaining: number | null;
  /** This tender is above the plan's budget ceiling — the reveal WILL be
   *  refused, so no button may be offered. */
  blockedByCeiling: boolean;
  /** Already opened; re-opening is free. */
  opened: boolean;
  /**
   * Present ONLY when this viewer has hit a per-account READ ceiling: the
   * contact is withheld even though they are entitled to it, and the card says
   * so calmly instead of drawing a wall.
   *
   * ⚠️ NOT `blockedByCeiling`, which is the ИП plan's BUDGET ceiling — a
   * permanent property of this lot for this plan, fixed by upgrading. This one
   * is temporary, is not about the lot, and an upgrade does nothing about it.
   * Showing either through the other's copy tells a paying customer to buy
   * something they already have.
   *
   * `resetAt` is an ISO instant and the day window is UTC — 05:00 in Tashkent —
   * so the copy states a TIME rather than saying "tomorrow". `limit` is the
   * ceiling that refused: it is env-configurable server-side, so it has to
   * travel rather than be written into the locale files.
   */
  limited?: { window: 'minute' | 'day'; resetAt: string; limit: number };
}

/** Full paginated response envelope returned by GET /api/catalog */
interface CatalogListEnvelope {
  data: CatalogRow[];
  meta: {
    pagination: { total: number; offset: number; limit: number };
    /** Envelope-level Pro flag for the requester — see CatalogListResult. */
    viewerIsPro?: boolean;
    /** See CatalogListResult.totalIsExact. Absent ⇒ exact (the list paths). */
    totalIsExact?: boolean;
    /** See CatalogListResult.nicheHints / nicheApplied. */
    nicheHints?: Array<{ slug: string; name: string }>;
    nicheApplied?: string;
    /** See CatalogListResult.orgHints. */
    orgHints?: Array<{ tin: string; kind: 'buyer' | 'supplier'; name: string | null }>;
    /** See CatalogListResult.lotNumberMatch. */
    lotNumberMatch?: string;
    /** See CatalogListResult.cpvCodeMatch. */
    cpvCodeMatch?: string[];
    /** See CatalogListResult.didYouMean. */
    didYouMean?: string;
  };
}

/** Raw API rows returned by the taxonomy endpoints. */
interface RegionRow {
  code: string;
  name: string;
  tenderCount?: number;
}
interface CategoryRow {
  slug: string;
  name: string;
  tenderCount?: number;
}

/**
 * The «пакет заявки уже собран» half of `row.bidPackage`, or nothing.
 *
 * Three things have to be true before a list is allowed to offer a download, and
 * each has bitten something on this codebase before:
 *
 *  - the wire value is the OBJECT form, not the detail page's `true`;
 *  - `ready` is actually true — a row that says `{ ready: false }` is a lot
 *    whose build failed or is still running, and a chip there would link to a
 *    404 while claiming the document is waiting;
 *  - the URL is ABSOLUTE. The API sends it relative to its own base
 *    (`/catalog/:id/bid-package/docx?lang=ru`), and the web is served from a
 *    different host — an href copied verbatim points at `uztender.com`, which
 *    answers the SPA shell with a 200, so nothing looks broken and no file
 *    arrives. Same normalisation the documents link on the detail page does.
 */
function readyBidPackage(value: CatalogRow['bidPackage']): { downloadUrl: string } | undefined {
  if (!value || typeof value !== 'object') return undefined;
  if (!value.ready || !value.downloadUrl) return undefined;
  const url = value.downloadUrl;
  return { downloadUrl: url.startsWith('http') ? url : `${apiBaseUrl}${url}` };
}

export function rowToItem(row: CatalogRow): CatalogItem {
  const categories = row.categories ?? [];
  return {
    id: row.id,
    title: sourceText(row.title),
    // Only ever present or absent, never null (a stale prerendered payload must not grow the key).
    ...(row.originalTitle ? { originalTitle: sourceText(row.originalTitle) } : {}),
    slug: row.slug ?? null,
    // The card's grey subtitle line prefers the AI summary, but falls back to the
    // raw source description (lot spec text) when there's no summary — otherwise
    // KZ/goszakup browse cards, which have no summaries, show a bare one-word
    // title and nothing else. Search results already carry a snippet in `summary`,
    // so this only changes the no-summary case.
    description: sourceText(row.summary || row.description || ''),
    detail: sourceText(row.description ?? ''),
    descriptionIsSourceFallback: row.descriptionIsSourceFallback ?? false,
    documentText: row.documentText ?? '',
    requirements: row.requirements ?? null,
    requirementsLocked: row.requirementsLocked ?? false,
    hasRequirements: row.hasRequirements ?? false,
    // ⚠️ `?? undefined`, never `?? null`: absent means "this API build predates
    // the states" and the panel must render as it always did, while an explicit
    // null from a newer API means "there is genuinely no criteria list". Folding
    // them together would paint an honest-empty state on every lot the moment
    // the web deployed ahead of the API.
    requirementsState: row.requirementsState ?? undefined,
    requirementsSource: row.requirementsSource ?? null,
    requirementsDisqualifying: row.requirementsDisqualifying ?? null,
    // Spread only when present, so a UZ/KZ item keeps exactly the keys it had.
    ...(row.awardCriteria?.length ? { awardCriteria: row.awardCriteria } : {}),
    requirementsSourceName: row.requirementsSourceName ?? null,
    hasDocuments: row.hasDocuments ?? false,
    // The locked file list a non-Pro viewer sees (names only, no links). Was
    // typed on both sides on 2026-09-06 but never copied here, so the page fell
    // through to the old three-line placeholder for every guest while the API
    // was already sending the real names — caught in prod, not by the tests,
    // which built the item directly.
    documentsPreview: row.documentsPreview ?? null,
    // Only ever `true` or absent, like `announcement`: a false flag and a
    // missing one both mean «no scan notice», and the list rows never carry it.
    ...(row.textFromScan === true ? { textFromScan: true as const } : {}),
    // Defaults TRUE, unlike the two flags above: absent means "old payload /
    // list view", and the flag's only job is to SUPPRESS the Pro wall. Missing
    // → assume there is a contact → keep the wall. Defaulting false would let a
    // stale prerendered payload silently strip the wall (and the CTA) off a gov
    // tender, which always has a contact.
    hasContact: row.hasContact ?? true,
    region: row.region?.name ?? row.region?.code ?? '',
    regionCode: row.region?.code ?? '',
    category: categories[0]?.name ?? categories[0]?.slug ?? '',
    categorySlug: categories[0]?.slug ?? '',
    categories,
    status: row.status,
    budgetUzs: row.budgetUzs ?? null,
    // Absent unless the API sent it (MiLicita only), like `announcement`.
    // At least one euro: an API one deploy behind still sends the 0,04 € placeholder
    // totals that printed «0 €» (QA 2026-10-21; `expediente-budget.ts` now drops them).
    ...(row.expedienteBudgetUzs && Number(row.expedienteBudgetUzs) >= 1
      ? { expedienteBudgetUzs: row.expedienteBudgetUzs }
      : {}),
    bidSecurityUzs: row.bidSecurityUzs ?? null,
    bidSecurityPct: row.bidSecurityPct ?? null,
    scope: row.scope ?? 'gov',
    noticeType: row.noticeType ?? null,
    publishedAt: '',
    deadline: row.deadline,
    // Only ever `true` or absent, never `false` — same reasoning as
    // `bidPackage` below: an absent flag and `false` mean the same thing (no
    // badge), and a stale prerendered payload must not grow the key.
    ...(row.announcement === true ? { announcement: true as const } : {}),
    // Same rule — `true` or absent (MiLicita only).
    ...(row.consultation === true ? { consultation: true as const } : {}),
    // Source attribution is Pro-only: the API OMITS `sourceKey`/`externalId`
    // for a non-entitled viewer (catalog.service.ts → `sourceRef`), because
    // together they resolve to the origin portal's lot page where the contacts
    // we sell are free. Preserve that absence instead of materialising an
    // explicit `null` — otherwise every SSR/prerendered page would still ship
    // `"sourceKey":null,"externalId":null` inside the dehydrated
    // `window.__RQ_STATE__`, re-advertising fields we deliberately withheld.
    ...(row.sourceKey !== undefined ? { sourceKey: row.sourceKey } : {}),
    ...(row.externalId !== undefined ? { externalId: row.externalId } : {}),
    // Same rule for the card's «Площадка · № лота» (Pro-only, omitted by the API).
    ...(row.sourceName != null ? { sourceName: row.sourceName } : {}),
    ...(row.lotNumber != null ? { lotNumber: row.lotNumber } : {}),
    ...(row.sourceUrl !== undefined ? { sourceUrl: row.sourceUrl } : {}),
    buyerName: row.buyerName ?? null,
    buyerTin: row.buyerTin ?? null,
    documents: row.documents ?? null,
    procedure:
      countryConfig.code === 'es' && typeof row.procedure?.announcementName === 'string'
        ? { ...row.procedure, announcementName: sourceText(row.procedure.announcementName) }
        : (row.procedure ?? null),
    lotDetails: row.lotDetails ?? null,
    // «Что закупают»: absent unless the API sent a non-empty list, so a list
    // row or an older payload grows no key in the dehydrated state.
    ...(Array.isArray(row.positions) && row.positions.length > 0
      ? { positions: row.positions }
      : {}),
    ...(typeof row.positionsTotal === 'number' ? { positionsTotal: row.positionsTotal } : {}),
    summaryLocked: row.summaryLocked ?? false,
    winnerName: row.winnerName ?? null,
    winnerInn: row.winnerInn ?? null,
    // The outcome wall's flag — absent unless set, like the paid keys below.
    ...(row.outcomeLocked ? { outcomeLocked: true as const } : {}),
    ...(row.outcomeLocked && typeof row.outcomeHasPrice === 'boolean'
      ? { outcomeHasPrice: row.outcomeHasPrice }
      : {}),
    ...(row.outcomeLocked && typeof row.outcomeHasContact === 'boolean'
      ? { outcomeHasContact: row.outcomeHasContact }
      : {}),
    // Copied outcome → `noindex` (`isTenderIndexable`). Absent unless set.
    ...(row.outcomeTransferred ? { outcomeTransferred: true as const } : {}),
    // Paid, and ABSENT unless the API sent it — same rule as the source pair
    // above: an explicit `null` would grow the key in every dehydrated
    // prerender payload.
    ...(row.winnerContact ? { winnerContact: row.winnerContact } : {}),
    ...(row.winnerContactCapped ? { winnerContactCapped: true } : {}),
    finalPriceUzs: row.finalPriceUzs ?? null,
    awardedAt: row.awardedAt ?? null,
    outcomeStatus: row.outcomeStatus ?? null,
    // Absent unless the API sent it (same rule as `outcomeTransferred`).
    ...(row.outcomeReason ? { outcomeReason: row.outcomeReason } : {}),
    siblingLots:
      row.siblingLots && countryConfig.code === 'es'
        ? row.siblingLots.map((sib) => ({ ...sib, title: sourceText(sib.title) }))
        : (row.siblingLots ?? undefined),
    siblingLotsTotal: row.siblingLotsTotal ?? undefined,
    siblingLotsTotalBudgetUzs: row.siblingLotsTotalBudgetUzs ?? null,
    contentChangedAt: row.contentChangedAt ?? null,
    unlocked: row.unlocked ?? false,
    freeUnlock: row.freeUnlock ?? null,
    contact: row.contact ?? null,
    contactAccess: row.contactAccess,
    // `|| undefined`, not `?? false`: the absence of the flag and an explicit
    // false mean the same thing here (no button), and keeping it undefined
    // means a list row never carries a capability the list cannot honour.
    // The object form is truthy too, and correctly so — only a viewer who holds
    // the grant is ever told that a package exists.
    bidPackage: row.bidPackage ? true : undefined,
    bidPackageReady: readyBidPackage(row.bidPackage),
    buyerTrust: row.buyerTrust ?? null,
  };
}

/** Serialize catalog filters to a query string (shared by list + export). */
export function buildCatalogQuery(filters: CatalogFilters = {}): string {
  const params = new URLSearchParams();
  if (filters.exclude) params.set('exclude', filters.exclude);
  if (filters.region) params.set('region', filters.region);
  if (filters.customer) params.set('customer', filters.customer);
  if (filters.category) params.set('category', filters.category);
  if (filters.ikpu) params.set('ikpu', filters.ikpu);
  if (filters.niche) params.set('niche', filters.niche);
  if (filters.kind) params.set('kind', filters.kind);
  if (filters.status) params.set('status', filters.status);
  if (filters.scope) params.set('scope', filters.scope);
  if (filters.ifiSource) params.set('ifiSource', filters.ifiSource);
  if (filters.q) params.set('q', filters.q);
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.minBudget != null) params.set('minBudget', String(filters.minBudget));
  if (filters.maxBudget != null) params.set('maxBudget', String(filters.maxBudget));
  if (filters.deadlineFrom) params.set('deadlineFrom', filters.deadlineFrom);
  if (filters.deadlineTo) params.set('deadlineTo', filters.deadlineTo);
  if (filters.since) params.set('since', filters.since);
  // Only ever sent as `true`. `awarded=false` is not a filter the API models
  // (it would read as "un-awarded", not "don't filter"), so a false/undefined
  // value must omit the param entirely rather than serialize.
  if (filters.awarded) params.set('awarded', 'true');
  if (filters.outcomeDays != null) params.set('outcomeDays', String(filters.outcomeDays));
  if (filters.criteria) params.set('criteria', filters.criteria);
  if (filters.alert) params.set('alert', filters.alert);
  if (filters.offset != null) params.set('offset', String(filters.offset));
  if (filters.limit != null) params.set('limit', String(filters.limit));
  // lang localizes each card's region + category name to the UI language.
  if (filters.lang) params.set('lang', filters.lang);
  return params.toString();
}

export async function fetchCatalog(filters: CatalogFilters = {}): Promise<CatalogListResult> {
  const qs = buildCatalogQuery(filters);
  const envelope = await apiRaw<CatalogListEnvelope>(`/catalog${qs ? `?${qs}` : ''}`);
  return {
    items: (envelope.data ?? []).map(rowToItem),
    total: envelope.meta?.pagination?.total ?? 0,
    offset: envelope.meta?.pagination?.offset ?? 0,
    limit: envelope.meta?.pagination?.limit ?? 20,
    viewerIsPro: envelope.meta?.viewerIsPro ?? false,
    // A plain COUNT(*) — nothing was capped.
    totalIsExact: envelope.meta?.totalIsExact ?? true,
    nicheHints: envelope.meta?.nicheHints ?? [],
    nicheApplied: envelope.meta?.nicheApplied,
    orgHints: envelope.meta?.orgHints ?? [],
    lotNumberMatch: envelope.meta?.lotNumberMatch,
    cpvCodeMatch: envelope.meta?.cpvCodeMatch,
  };
}

/** Serialize search filters to a query string (mirrors `buildCatalogQuery`,
 *  scoped to the subset GET /catalog/search accepts). Extracted so the
 *  `scope` guard below is unit-testable directly against the built query
 *  string, without going through the network call or a mock of this module. */
export function buildSearchQuery(filters: CatalogFilters = {}): string {
  const params = new URLSearchParams();
  // Must be in BOTH builders. The catalog page swaps endpoints the moment a
  // query is typed, so a filter serialized by only one of them silently
  // disappears mid-session — which is exactly the "the filter did nothing"
  // report this axis exists to answer.
  if (filters.exclude) params.set('exclude', filters.exclude);
  if (filters.q) params.set('q', filters.q);
  if (filters.region) params.set('region', filters.region);
  if (filters.category) params.set('category', filters.category);
  if (filters.kind) params.set('kind', filters.kind);
  if (filters.ikpu) params.set('ikpu', filters.ikpu);
  if (filters.niche) params.set('niche', filters.niche);
  if (filters.status) params.set('status', filters.status);
  // Buyer (заказчик) ИНН — Task 7b. The alert matcher already ANDs
  // text + customerTin (compileCriteria); before this, `GET /catalog` (no
  // keyword) honoured a buyer pin but `GET /catalog/search` silently dropped
  // it, so a saved-search preview and a live search of the same criteria
  // disagreed the moment a keyword was typed.
  if (filters.customer) params.set('customer', filters.customer);
  // Same invariant as buildCatalogQuery: only sent when a caller set one, so
  // the server's own default (gov + corporate) keeps applying everywhere else.
  if (filters.scope) params.set('scope', filters.scope);
  // #53 — поиск теперь умеет фильтровать по институту (подзапрос на
  // tender_sources в трёх построителях), поэтому чип доезжает и сюда.
  if (filters.ifiSource) params.set('ifiSource', filters.ifiSource);
  // The filter `/results` IS. The endpoint had no `awarded` parameter at all, so
  // typing a query on the awarded archive dropped it and the page collapsed to
  // whatever the default active+closing scope matched. Same serialization rule
  // as buildCatalogQuery: only ever sent as `true`.
  if (filters.awarded) params.set('awarded', 'true');
  // Budget bounds likewise — the search endpoint routes a bounded request to the
  // list engine rather than ignoring the bound.
  if (filters.minBudget != null) params.set('minBudget', String(filters.minBudget));
  if (filters.maxBudget != null) params.set('maxBudget', String(filters.maxBudget));
  if (filters.lang) params.set('lang', filters.lang);
  if (filters.offset != null) params.set('offset', String(filters.offset));
  if (filters.limit != null) params.set('limit', String(filters.limit));
  return params.toString();
}

/**
 * Semantic + keyword hybrid search via GET /catalog/search (ranked by relevance).
 * Used when the user types a query. Supports region / category / ikpu / niche /
 * status / kind / scope / lang (see `buildSearchQuery` above); `sort` and the
 * budget/deadline range filters don't apply while searching (relevance order,
 * no range-filter support server-side).
 */
export async function fetchSemanticSearch(
  filters: CatalogFilters = {},
): Promise<CatalogListResult> {
  const envelope = await apiRaw<CatalogListEnvelope>(
    `/catalog/search?${buildSearchQuery(filters)}`,
  );
  return {
    items: (envelope.data ?? []).map(rowToItem),
    total: envelope.meta?.pagination?.total ?? 0,
    offset: envelope.meta?.pagination?.offset ?? 0,
    limit: envelope.meta?.pagination?.limit ?? 20,
    // GET /catalog/search resolves the same canonical entitlement.isPro check
    // as the plain list (SearchController → SearchService.search), so a Pro
    // visitor searching within a locked layer (e.g. `/ifi?q=...`) sees the
    // same unlocked cards the unfiltered list gives them.
    viewerIsPro: envelope.meta?.viewerIsPro ?? false,
    // Only this endpoint can answer `false` — see CatalogListResult.
    totalIsExact: envelope.meta?.totalIsExact ?? true,
    nicheHints: envelope.meta?.nicheHints ?? [],
    nicheApplied: envelope.meta?.nicheApplied,
    orgHints: envelope.meta?.orgHints ?? [],
    lotNumberMatch: envelope.meta?.lotNumberMatch,
    cpvCodeMatch: envelope.meta?.cpvCodeMatch,
    didYouMean: envelope.meta?.didYouMean,
  };
}

/** Headline counts for the catalog header chips (GET /catalog/stats). */
export interface CatalogStats {
  active: number;
  closingThisWeek: number;
  newToday: number;
}

/** `scope` mirrors {@link CatalogFilters.scope} — undefined lets the API apply
 *  its own default (gov + corporate); a fixed-layer page (e.g. /ifi) MUST pass
 *  its scope or the header chips silently report the gov catalog's numbers. */
export async function fetchCatalogStats(scope?: string): Promise<CatalogStats> {
  return apiRequest<CatalogStats>(`/catalog/stats${scope ? `?scope=${scope}` : ''}`);
}

/** Scope + kind facet counts for a search query (GET /catalog/search/facets).
 *  `scope.all` = gov+corporate (the default browse set), plus `ifi` and
 *  `supply`. `kind.all` is the grand total (kind-less lots included), so it
 *  equals the search total, not the sum of the three named kinds.
 *  `totalIsExact=false` means each number is a lower bound («не менее»), same as
 *  the search total when its k-NN arm fills its cap. */
export interface SearchFacets {
  scope: { all: number; ifi: number; supply: number };
  kind: { all: number; goods: number; works: number; services: number };
  totalIsExact: boolean;
}

/** Feeds the search results' scope filter chips and the kind tabs' query
 *  counts. Only the filters the search actually honours are sent (no scope/kind
 *  — the breakdown spans every layer and every kind; no sort/range — search
 *  ignores those). */
export async function fetchSearchFacets(filters: {
  q: string;
  region?: string;
  category?: string;
  status?: string;
  ikpu?: string;
  niche?: string;
  customer?: string;
  /** Минус-слова через запятую — ДОЛЖНЫ уходить вместе с запросом: без них
   *  бейджи вкладок считают набор без минус-слов и расходятся со списком под
   *  ними (замер на проде 26.09.2026: 67 в бейдже против 20 в выдаче). */
  exclude?: string;
  lang?: string;
}): Promise<SearchFacets> {
  const params = new URLSearchParams();
  params.set('q', filters.q);
  for (const [key, value] of [
    ['region', filters.region],
    ['category', filters.category],
    ['status', filters.status],
    ['ikpu', filters.ikpu],
    ['niche', filters.niche],
    ['customer', filters.customer],
    ['exclude', filters.exclude],
    ['lang', filters.lang],
  ] as const) {
    if (value) params.set(key, value);
  }
  return apiRequest<SearchFacets>(`/catalog/search/facets?${params.toString()}`);
}

/** Счётчики по типам закупки при текущих остальных фильтрах (см. GET /catalog/kind-counts). */
export async function fetchKindCounts(filters: CatalogFilters = {}): Promise<KindCounts> {
  // `kind` намеренно не отправляем: счётчики нужны по всем трём вкладкам сразу.
  const qs = buildCatalogQuery({
    ...filters,
    kind: undefined,
    offset: undefined,
    limit: undefined,
  });
  return apiRequest<KindCounts>(`/catalog/kind-counts${qs ? `?${qs}` : ''}`);
}

/** Счётчики по институтам при текущих остальных фильтрах (#53). */
export async function fetchIfiSourceCounts(filters: CatalogFilters = {}): Promise<IfiSourceCounts> {
  // `ifiSource` намеренно не отправляем: числа нужны для всех чипов сразу.
  const qs = buildCatalogQuery({
    ...filters,
    ifiSource: undefined,
    offset: undefined,
    limit: undefined,
  });
  return apiRequest<IfiSourceCounts>(`/catalog/ifi/source-counts${qs ? `?${qs}` : ''}`);
}

export async function fetchTender(id: string, lang?: string): Promise<CatalogItem> {
  const row = await apiRequest<CatalogRow>(`/catalog/${id}${lang ? `?lang=${lang}` : ''}`);
  return rowToItem(row);
}

/**
 * Spend this month's free unlock on this tender. Only a viewer whose
 * `freeUnlock.eligible` is true and not yet `unlocked` should ever call this —
 * see {@link CatalogItem.freeUnlock}. Returns the tender re-fetched with the
 * contact now inline, mirroring {@link fetchTender}'s shape.
 *
 * Throws ApiError on ineligibility (e.g. `free_unlock_exhausted`,
 * `free_unlock_ineligible`) — the same error-code convention as
 * {@link revealContact}.
 */
export async function unlockTender(id: string): Promise<CatalogItem> {
  const row = await apiRequest<CatalogRow>(`/tenders/${id}/unlock`, { method: 'POST' });
  return rowToItem(row);
}

/** Neighbours of a tender by embedding similarity, biddable ones first.
 *
 *  **Public on purpose** — no auth header is required and none is sent by the
 *  prerender. The whole point of the block is that it reaches the crawled DOM;
 *  an authed-only variant would be invisible to Googlebot exactly as
 *  `BaseRatesPanel` is. */
export async function fetchSimilarTenders(id: string, lang?: string): Promise<CatalogItem[]> {
  const rows = await apiRequest<CatalogRow[]>(
    `/catalog/${id}/similar${lang ? `?lang=${lang}` : ''}`,
  );
  return rows.map(rowToItem);
}

/** Which rung of the ЕНКТ ladder filled «Похожие открытые лоты» — see
 *  `services/api/src/modules/catalog/similar-live.ts`. */
export type SimilarLiveLevel = 'exact' | 'class' | 'group' | 'section' | 'category';

export interface SimilarLive {
  items: CatalogItem[];
  level: SimilarLiveLevel | null;
  /** What «Следить за такими» subscribes to — exactly one key; null with an
   *  empty block. */
  follow: { ikpu: string } | { category: string } | null;
}

/** «Похожие открытые лоты» for a lot that closed without an outcome: 3–5 lots
 *  still open for bids, same ЕНКТ code widened step by step, else the same
 *  category. Public — navigation, not Pro data. Called client-side only: the
 *  pages it serves are not prerendered, and this must not change that. */
export async function fetchSimilarLive(id: string, lang?: string): Promise<SimilarLive> {
  const res = await apiRequest<{
    items: CatalogRow[];
    level: SimilarLiveLevel | null;
    follow: SimilarLive['follow'];
  }>(`/catalog/${id}/similar-live${lang ? `?lang=${lang}` : ''}`);
  return { items: res.items.map(rowToItem), level: res.level, follow: res.follow };
}

/** Full (untruncated) UZ + RU AI summary for a tender — Pro-only; throws
 *  ApiError('pro_required') otherwise. */
export async function fetchFullSummary(
  id: string,
): Promise<{ uz: string | null; ru: string | null }> {
  return apiRequest<{ uz: string | null; ru: string | null }>(`/tenders/${id}/summary`);
}

/**
 * Spend one contact open on this tender and get the заказчик contact back.
 *
 * Only a `metered` viewer ever calls this: a full-access viewer already has the
 * contact inline, and a walled one has no opens to spend. Re-revealing a tender
 * already opened is free and returns the same contact.
 *
 * The response also carries the SOURCE reference: the open buys the link to the
 * original notice as well as the contact, and on a lot whose заказчик published
 * no contact that link is the whole of what it buys. The three keys are OMITTED
 * (not nulled) for a viewer not entitled to them — the same posture the detail
 * payload takes. The page refetches the tender after a reveal, so these are a
 * convenience rather than the only route to the link.
 *
 * Throws ApiError with `plan_budget_ceiling` (tender above the plan's limit) or
 * `pro_required` (monthly allowance exhausted).
 */
export async function revealContact(id: string): Promise<{
  name: string | null;
  email: string | null;
  phone: string | null;
  externalId?: string | null;
  sourceKey?: string | null;
  sourceUrl?: string | null;
}> {
  return apiRequest(`/tenders/${id}/contacts`, { method: 'POST' });
}

/** Pro-gated .xlsx export of the current filtered result set. Triggers a browser
 *  download. Throws the server error `code` (e.g. 'pro_required') on failure. */
export async function downloadCatalogExport(filters: CatalogFilters = {}): Promise<void> {
  const qs = buildCatalogQuery(filters);
  const res = await fetch(`${apiBaseUrl}/catalog/export${qs ? `?${qs}` : ''}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      errors?: Array<{ code?: string }>;
    } | null;
    throw new Error(body?.errors?.[0]?.code ?? 'export_failed');
  }
  saveBlob(await res.blob(), 'tenders.xlsx');
}

/**
 * Pro «Экспорт в Excel» on /results — the winners of the last month matching
 * the card (GET /catalog/export-results). The API pins the set (awarded, the
 * month window, newest first, ≤1000 rows) and the gate (`pro_required`, the
 * same as the lots export); the file has no phones or e-mails.
 */
export async function downloadResultsExport(input: {
  criteria?: string;
  lang?: string;
}): Promise<void> {
  const qs = buildCatalogQuery({ criteria: input.criteria, lang: input.lang });
  const res = await fetch(`${apiBaseUrl}/catalog/export-results${qs ? `?${qs}` : ''}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      errors?: Array<{ code?: string }>;
    } | null;
    throw new Error(body?.errors?.[0]?.code ?? 'export_failed');
  }
  saveBlob(await res.blob(), 'results.xlsx');
}

/** «Найдено N результатов за месяц · M за неделю · K сегодня» — GET /catalog/awarded-summary.
 *  Always the awarded archive (the API pins it and its three windows);
 *  `criteria` is the card. */
export async function fetchAwardedSummary(
  criteria: string | undefined,
): Promise<{ month: number; week: number; today: number }> {
  const qs = buildCatalogQuery({ criteria });
  return apiRequest<{ month: number; week: number; today: number }>(
    `/catalog/awarded-summary${qs ? `?${qs}` : ''}`,
  );
}

export interface AwardedSuggestions {
  categories: Array<{ slug: string; name: string; count: number }>;
  ikpu: Array<{ code: string; name: string; count: number }>;
}

/** Category + ИКПУ suggestions under the /results chip input, with their
 *  counts in the awarded archive — GET /catalog/awarded-suggest. */
export async function fetchAwardedSuggest(q: string, lang?: string): Promise<AwardedSuggestions> {
  const params = new URLSearchParams({ q: q.trim() });
  if (lang) params.set('lang', lang);
  const r = await apiRequest<AwardedSuggestions>(`/catalog/awarded-suggest?${params.toString()}`);
  return { categories: r?.categories ?? [], ikpu: r?.ikpu ?? [] };
}

export async function fetchRegions(lang?: string): Promise<RegionItem[]> {
  const rows = await apiRequest<RegionRow[]>(`/catalog/regions${lang ? `?lang=${lang}` : ''}`);
  // The API orders by ISO code (AND, BUK, FER, JIZ…), which in Russian reads as
  // Андижанская, Бухарская, Ферганская, Джизакская — Latin order under Cyrillic
  // labels (friend's audit, 2026-09-07). Sort by the localized name instead.
  const collator = new Intl.Collator(lang === 'uz' ? 'uz' : 'ru');
  return (rows ?? [])
    .map((r) => ({ slug: r.code, name: r.name, count: r.tenderCount ?? 0 }))
    .sort((a, b) => collator.compare(a.name, b.name));
}

/** The small IT/telecom categories are merged into one prominent "IT & Digital"
 *  facet so IT reads as a single substantial category, not two thin ones. The
 *  slug is the comma-joined union the catalog API filters on (`slug IN (...)`). */
export const IT_DIGITAL_MEMBERS = ['it-telecom', 'telecom-equipment'];
export const IT_DIGITAL_SLUG = IT_DIGITAL_MEMBERS.join(',');

function itDigitalLabel(lang?: string): string {
  // MiLicita's only locale. UZ/KZ (ru/uz/kk) keep their labels unchanged.
  if (lang === 'es') return 'Informática y telecomunicaciones';
  return lang === 'ru' ? 'ИТ и цифровые технологии' : 'IT va raqamli texnologiyalar';
}

/** Merge the IT member categories into a single "IT & Digital" grouping (summing
 *  counts). A no-op when none of the members are present. */
export function groupItDigital(items: CategoryItem[], lang?: string): CategoryItem[] {
  const members = items.filter((c) => IT_DIGITAL_MEMBERS.includes(c.slug));
  if (members.length === 0) return items;
  const count = members.reduce((sum, c) => sum + c.count, 0);
  const rest = items.filter((c) => !IT_DIGITAL_MEMBERS.includes(c.slug));
  return [...rest, { slug: IT_DIGITAL_SLUG, name: itDigitalLabel(lang), count }];
}

/**
 * Broad category facets with their tender counts.
 *
 * `filters` are the caller's ACTIVE catalog filters: the API computes each
 * category's count under them (minus the ones the click discards — see
 * {@link optionCountFilters}), so the number on a row equals the number of
 * results picking it yields. Omitting them is safe (the save-search dialog and
 * onboarding browse the whole catalog).
 */
export async function fetchCategories(
  lang?: string,
  filters: CatalogFilters = {},
): Promise<CategoryItem[]> {
  const qs = buildCatalogQuery({ ...optionCountFilters(filters), lang });
  const rows = await apiRequest<CategoryRow[]>(`/catalog/categories${qs ? `?${qs}` : ''}`);
  const items = (rows ?? []).map((c) => ({
    slug: c.slug,
    name: c.name,
    count: c.tenderCount ?? 0,
  }));
  return groupItDigital(items, lang);
}

/**
 * Category facets for the /directory hub index — the SAME request as
 * {@link fetchCategories} but WITHOUT the IT grouping.
 *
 * `groupItDigital` folds `it-telecom` + `telecom-equipment` into one synthetic row
 * whose slug is the comma-joined filter value `it-telecom,telecom-equipment`, and
 * REMOVES both members. That is right for a filter control and wrong for a list of
 * hub links: the synthetic slug is not a route (`/category/it-telecom,telecom-equipment`
 * is neither prerendered nor in the sitemap), and dropping the members would leave
 * two real hubs orphaned — on the page whose entire job is to un-orphan hubs.
 *
 * Kept as its own function rather than a flag on `fetchCategories` so the two
 * callers cannot drift: every filter UI wants the grouping, the hub index never does.
 */
export async function fetchCategoryHubs(lang?: string): Promise<CategoryItem[]> {
  const qs = buildCatalogQuery({ lang });
  const rows = await apiRequest<CategoryRow[]>(`/catalog/categories${qs ? `?${qs}` : ''}`);
  return (rows ?? []).map((c) => ({
    slug: c.slug,
    name: c.name,
    count: c.tenderCount ?? 0,
  }));
}

export interface IkpuItem {
  /** ИКПУ code used as the catalog `ikpu` filter value (e.g. "26.2"). */
  code: string;
  name: string;
  level: number;
  /** HIERARCHICAL — a code's count contains its sub-codes'. Sibling rows sum to
   *  their parent's; a parent and its descendant do not sum to anything. */
  count: number;
  /**
   * Nearest ancestor of this code that is ALSO in the same list, or null/absent
   * when the row is a root there. The API decides this (one shared containment
   * rule, next to the counts it must agree with) — the UI must never re-derive
   * it from the code string.
   */
  parentCode?: string | null;
  /** `parentCode`'s count — the total this row's `count` is a part of. */
  parentCount?: number | null;
  /** Ancestors of this row present in the list: 0 = root, 1 = its child, … */
  depth?: number;
  /**
   * Set when `name` belongs to an ANCESTOR, not to this code — the API had no
   * row for the code and could derive nothing from its children. Holds that
   * ancestor's code. Render the row as "all codes under X"; printing the
   * borrowed name states something the classifier may contradict (`58.29` came
   * back as «Издательская деятельность», the name of division 58, where ОКЭД
   * has «Выпуск прочего программного обеспечения»).
   */
  nameInheritedFrom?: string | null;
  /** `name` came from a lot title, not a classifier — right ballpark, not
   *  necessarily this code's own label. Show it as context, not as the name. */
  nameIsApproximate?: boolean;
  /**
   * The top-level ИКПУ SECTION this code sits under — itself, when it IS one
   * — independent of `parentCode` above (which only knows about codes present
   * in THIS same list). A search hit routinely has no ancestor among the
   * other hits, so `parentCode` is null there even though the code plainly
   * belongs to a section; `sectionCode` is what makes a bare code like
   * "45.31.1" locatable once search flattens the collapsible browse grouping.
   * Decided server-side, same as `parentCode` — never re-derived by splitting
   * `code` on its dots. Only populated on search results; the full facet list
   * (`fetchIkpuCodes`) doesn't need it — a row's own `depth === 0` there
   * already means it IS a section.
   */
  sectionCode?: string | null;
  sectionName?: string | null;
}

interface IkpuRow {
  code: string;
  name: string;
  level?: number;
  tenderCount?: number;
  parentCode?: string | null;
  parentTenderCount?: number | null;
  nestDepth?: number;
  nameInheritedFrom?: string | null;
  nameIsApproximate?: boolean;
  sectionCode?: string | null;
  sectionName?: string | null;
}

/**
 * The filters a filter-panel option's count must be computed under —
 * everything that SURVIVES clicking the option, and nothing that doesn't.
 * Shared by all three lists in the panel (category · niche · ИКПУ) so they
 * cannot drift apart.
 *
 * Dropped on purpose:
 *  - `q` — picking a classifier CLEARS the text query (see `selectCategory` /
 *    `selectNiche` / `selectIkpu`), so counting with it would promise an
 *    intersection the click never delivers. This is the exact defect where the
 *    dropdown said 3 and the result said 0.
 *  - `category` / `ikpu` / `niche` — one shared axis; the click REPLACES
 *    whatever is on it.
 *  - `sort` / `offset` / `limit` — presentation, not membership.
 */
export function optionCountFilters(filters: CatalogFilters = {}): CatalogFilters {
  const {
    q: _q,
    category: _category,
    ikpu: _ikpu,
    niche: _niche,
    sort: _sort,
    offset: _offset,
    limit: _limit,
    lang: _lang,
    ...surviving
  } = filters;
  return surviving;
}

/** ИКПУ search-as-you-type for the niche picker (onboarding + filter). Matches
 *  code prefix or name (uz/ru), ranked by live-tender count. Blank query → [].
 *
 *  `filters` are the caller's ACTIVE catalog filters: the API computes each
 *  option's count under them (minus the ones the click discards — see
 *  {@link optionCountFilters}), so the number on a row equals the number of
 *  results picking it yields. Omitting them is safe (onboarding has none). */
export async function searchIkpu(
  q: string,
  lang?: string,
  limit = 20,
  filters: CatalogFilters = {},
): Promise<IkpuItem[]> {
  const term = q.trim();
  if (!term) return [];
  const params = new URLSearchParams(
    buildCatalogQuery({ ...optionCountFilters(filters), q: term, limit, lang }),
  );
  const rows = await apiRequest<IkpuRow[]>(`/catalog/ikpu/search?${params.toString()}`);
  return (rows ?? []).map((r) => ({
    code: r.code,
    name: r.name,
    level: r.level ?? 1,
    count: r.tenderCount ?? 0,
    parentCode: r.parentCode ?? null,
    parentCount: r.parentTenderCount ?? null,
    depth: r.nestDepth ?? 0,
    nameInheritedFrom: r.nameInheritedFrom ?? null,
    nameIsApproximate: r.nameIsApproximate ?? false,
    sectionCode: r.sectionCode ?? null,
    sectionName: r.sectionName ?? null,
  }));
}

export interface NicheItem {
  slug: string;
  name: string;
  codes: string[];
  tenderCount: number;
}

/** Niches (Spec #3 classification layer), ranked by live-tender count.
 *  With a query: matches niche name (uz/ru) or its underlying ИКПУ codes.
 *  BLANK query: browses all of them — deliberately NOT [], unlike `searchIkpu`,
 *  which has its own `fetchIkpuCodes` browse facet. Takes `filters` so a niche
 *  row's count is what clicking it actually returns. */
export async function searchNiches(
  q: string,
  lang?: string,
  filters: CatalogFilters = {},
): Promise<NicheItem[]> {
  // A BLANK term browses every niche — it does NOT short-circuit to [].
  //
  // Both ends used to bail on an empty query, so niches were reachable only by
  // typing their name: you had to know one existed to find it. The API now
  // returns all 23 ranked by live count for a blank term, and this must not
  // swallow that.
  const term = q.trim();
  const params = new URLSearchParams(
    buildCatalogQuery({ ...optionCountFilters(filters), ...(term ? { q: term } : {}), lang }),
  );
  const rows = await apiRequest<NicheItem[]>(`/catalog/niche/search?${params.toString()}`);
  return rows ?? [];
}

/** Resolve a `?niche=<slug>` deep-link to its full item (localized name + live
 *  count) so the filter trigger shows the niche name, not the raw slug. `null`
 *  for an unknown/blank slug. Mirrors ИКПУ's on-load code resolve. */
export async function resolveNiche(
  slug: string,
  lang?: string,
  scope?: string,
): Promise<NicheItem | null> {
  const s = slug.trim();
  if (!s) return null;
  const params = new URLSearchParams({ slug: s });
  if (lang) params.set('lang', lang);
  if (scope) params.set('scope', scope);
  return apiRequest<NicheItem | null>(`/catalog/niche/resolve?${params.toString()}`);
}

/**
 * The full ИКПУ facet list — every code with a live tender, ranked by count
 * and nested depth-first (`markIkpuNesting` server-side), same shape
 * `searchIkpu` returns. Powers the picker's collapsible browse view: a row
 * with `depth === 0` is a top-level SECTION, and everything between one such
 * row and the next is (transitively) its subtree — see `subject-picker.tsx`.
 */
/**
 * The ИКПУ browse list for the picker.
 *
 * `filters` are the caller's ACTIVE catalog filters — the API computes each
 * code's count under them, minus the ones the click itself discards (see
 * {@link optionCountFilters}), exactly as `fetchCategories` and `searchIkpu`
 * already do. It used to take `lang` alone, which is why the ИКПУ half of the
 * picker showed whole-catalogue totals while the categories half above it
 * re-scoped correctly: same popover, two different numbers for the same
 * question.
 */
/**
 * The names of `codes` off the FULL code table (`GET /catalog/ikpu/names`) —
 * for chips, which must name every code a search holds, not only the popular
 * ones {@link fetchIkpuCodes} lists. A map, code → name; a code the table does
 * not hold is absent (the caller keeps its fallback).
 */
export async function fetchIkpuNames(
  codes: readonly string[],
  lang?: string,
): Promise<Record<string, string>> {
  if (codes.length === 0) return {};
  const params = new URLSearchParams({ codes: codes.join(',') });
  if (lang) params.set('lang', lang);
  const rows = await apiRequest<Array<{ code: string; name: string }>>(
    `/catalog/ikpu/names?${params.toString()}`,
  );
  return Object.fromEntries((rows ?? []).map((r) => [r.code, r.name]));
}

export async function fetchIkpuCodes(
  lang?: string,
  filters: CatalogFilters = {},
): Promise<IkpuItem[]> {
  const qs = buildCatalogQuery({ ...optionCountFilters(filters), lang });
  const rows = await apiRequest<IkpuRow[]>(`/catalog/ikpu${qs ? `?${qs}` : ''}`);
  return (rows ?? []).map((r) => ({
    code: r.code,
    name: r.name,
    level: r.level ?? 1,
    count: r.tenderCount ?? 0,
    parentCode: r.parentCode ?? null,
    parentCount: r.parentTenderCount ?? null,
    depth: r.nestDepth ?? 0,
  }));
}

export interface BaseRatesView {
  ikpu: string;
  sample: number;
  dateRange: { from: string; to: string } | null;
  participants: { median: number; n: number; modeSharePct: number } | null;
  discountPct: { median: number; n: number; modeSharePct: number } | null;
  winningPriceUzs: { p25: number; median: number; p75: number; n: number } | null;
  /**
   * True when the comparables were restricted to a budget band around this lot
   * (0.3×–3× of its budget) — a like-for-like comparison. False means the sample
   * is the whole ИКПУ section regardless of lot size, which the panel MUST
   * label: sections mix 30 M lots with 5 bn ones.
   *
   * ⚠️ Optional on the wire so a client built against an older API still
   * renders; `undefined` is read as un-banded, i.e. the cautious label.
   */
  banded?: boolean;
  /** Absolute so'm bounds of that band, for «цена победителя X – Y». */
  band?: { minUzs: number; maxUzs: number } | null;
  /** This lot's budget against the comparables' p25–p75 price range. */
  budgetPosition?: 'below' | 'inside' | 'above' | null;
}
/**
 * The LOCKED branch carries non-decision counts for the blurred teaser: HOW
 * MANY comparables the panel is built from (already capped at the 500-row
 * sample the unlocked card uses) and OVER WHAT PERIOD. Never a median, a
 * discount, a price or a range — those are the product.
 *
 * ⚠️ Optional on the wire so a client built against an older API still renders
 * the card; `undefined` simply drops the basis line, never the teaser.
 */
export type BaseRatesResponse =
  | {
      locked: true;
      hasData: boolean;
      comparableCount?: number;
      dateRange?: { from: string; to: string } | null;
      /** Which metrics the unlocked panel shows — booleans only. The teaser
       *  draws a blurred cell for exactly these (2026-10-15); absent on an
       *  older API, which draws all of them as before. */
      metrics?: { participants: boolean; discount: boolean; price: boolean };
      /** Spain only (`features.freeBidderMedian`): the ONE real figure of the
       *  teaser — the comparables' median bidder count, over `n` lots (≥ 5). */
      bidders?: { median: number; n: number };
      /** Spain only: how many comparables carry a discount (≥ 5) — a COUNT for
       *  «Baja mediana en N lotes parecidos», never the discount. */
      discountLots?: number;
    }
  | { locked: false; hasData: boolean; rates: BaseRatesView | null };

/** Paid analytics for a tender (GET /catalog/:id/base-rates). Throws 401 when anonymous. */
export async function getBaseRates(id: string): Promise<BaseRatesResponse> {
  return apiRequest<BaseRatesResponse>(`/catalog/${id}/base-rates`);
}

export interface SupplierRef {
  tin: string;
  name: string | null;
  contracts: number;
  valueUzs: number;
  sharePct: number;
  /** Newest award with this buyer (ISO) — «Показать всех» (2026-10-18).
   *  Optional: an older API does not send it. */
  lastAwardAt?: string | null;
  /** That date is a bid deadline standing in for an unpublished award date. */
  lastAwardApprox?: boolean;
}
export interface BuyerConcentration {
  buyerTin: string;
  nContracts: number;
  nSuppliers: number;
  totalValueUzs: number;
  suppliers: SupplierRef[];
  topSupplierSharePct: number;
  /** Contracts behind `topSupplierSharePct` — the share's denominator. */
  topSupplierContracts: number;
  hhi: number;
  singleSupplierDependence: boolean;
  /** True when `suppliers` is the full list (`?all=1`), newest award first. */
  all?: boolean;
}
/** The guest / free teaser of a company card (owner, 2026-10-18): the REAL
 *  names of the first three companies in the card's order — a name only, never
 *  an ИНН, share, date, sum or count. */
export interface TeaserCompany {
  name: string;
}

/** Locked: the SIZE of the buyer's award history (contracts, distinct
 *  suppliers), never its SHAPE — no share, no HHI — plus the top-3 supplier
 *  names (`teaser`). Optional on the wire for the same forward-compat reason as
 *  `BaseRatesResponse`. */
export type BuyerGraphResponse =
  | {
      locked: true;
      hasData: boolean;
      contractCount?: number;
      supplierCount?: number;
      teaser?: TeaserCompany[];
    }
  | { locked: false; hasData: boolean; graph: BuyerConcentration | null };

/** Paid analytics for a tender's buyer (GET /catalog/:id/buyer-graph). The
 *  route is public: a guest gets the locked payload. `all` — «Показать всех
 *  (N)»: the suppliers with the newest award (capped server-side), newest
 *  first. */
export async function getBuyerGraph(
  id: string,
  opts: { all?: boolean } = {},
): Promise<BuyerGraphResponse> {
  return apiRequest<BuyerGraphResponse>(`/catalog/${id}/buyer-graph${opts.all ? '?all=1' : ''}`);
}

/** «Вероятные участники» (GET /catalog/:id/similar-winners) — mirrors
 *  `similar-winners.ts` in the API. Locked viewers get two counts, never names. */
export interface SimilarWinner {
  tin: string;
  name: string | null;
  /** Contracts in the window, competitive and direct. */
  wins: number;
  totalUzs: number;
  sharePct: number;
  /** Newest contract (ISO). Optional fields: an older API does not send them. */
  lastAwardAt?: string | null;
  /** That date is a bid deadline standing in for an unpublished award date. */
  lastAwardApprox?: boolean;
  directWins?: number;
  /** «торги» / «напрямую» / both. */
  kind?: 'tender' | 'direct' | 'mixed';
}
export interface SimilarWinners {
  code: string;
  tier: 'exact' | 'class';
  classCode: string | null;
  awards: number;
  winnerCount: number;
  windowMonths: number;
  winners: SimilarWinner[];
  /** True when `winners` is the full list (`?all=1`), newest contract first. */
  all?: boolean;
}
export type SimilarWinnersResponse =
  | {
      locked: true;
      hasData: boolean;
      awards: number;
      winnerCount: number;
      /** First three names, the card's order (absent from an older API). */
      teaser?: TeaserCompany[];
    }
  // `result`, not `data`: a top-level `data` key skips the API's envelope and
  // `apiRequest` would unwrap the aggregate itself (2026-10-10 regression).
  | { locked: false; hasData: boolean; result: SimilarWinners | null };

export async function getSimilarWinners(
  id: string,
  opts: { all?: boolean } = {},
): Promise<SimilarWinnersResponse> {
  const qs = opts.all ? '?all=1' : '';
  return apiRequest<SimilarWinnersResponse>(`/catalog/${id}/similar-winners${qs}`);
}

/** Per-tender integrity facts (mirrors the API's TenderIntegrity shape). */
export interface TenderIntegrity {
  relisting: {
    externalId: string;
    outcome: 'closed_no_winner' | 'awarded' | 'unknown';
    participantCount: number | null;
  } | null;
  participantsLive: number | null;
  /** True once bidding is closed — only then is 0 or 1 registered a signal.
   *  Optional to keep older fixtures compiling; absent reads as final. */
  participantsFinal?: boolean;
  deposit: { pct: number; sectionMedianPct: number | null; elevated: boolean } | null;
  requirements: {
    disqualifyingCount: number;
    /** The disqualifying criteria by name (may be shorter than the count when
     *  the source left some nameless). Optional for the same fixture reason. */
    disqualifying?: string[];
    techWeight: number | null;
    costWeight: number | null;
  } | null;
  hasAny: boolean;
}

/** Locked: how many of the four checks have inputs on this lot. What they
 *  FOUND (re-listing, deposit pct, participants, criteria) stays gated. */
export type TenderIntegrityResponse =
  | { locked: true; hasData: boolean; checksCount?: number }
  | { locked: false; integrity: TenderIntegrity | null };

/** Paid integrity facts for a tender (GET /catalog/:id/integrity). Throws 401 when anonymous. */
export async function getTenderIntegrity(id: string): Promise<TenderIntegrityResponse> {
  return apiRequest<TenderIntegrityResponse>(`/catalog/${id}/integrity`);
}

// ── Supplier profile (Pro) ─────────────────────────────────────────────
export interface SupplierBuyerRef {
  tin: string;
  name: string | null;
  contracts: number;
  valueUzs: number;
}
export interface SupplierCategoryRef {
  section: string;
  name: string | null;
  contracts: number;
  valueUzs: number;
}
export interface SupplierRegionRef {
  region: string;
  contracts: number;
  /** `regions.code` — the key to `/region/:code`. Optional/null when the free-text
   *  `organizations.region` name does not resolve to a reference row; the UI then
   *  renders plain text rather than a dead link. */
  code?: string | null;
}
export interface SupplierDebarredInfo {
  reason: string | null;
  dateBegin: string | null;
  dateEnd: string | null;
}
/**
 * Mirrors the API's `SettlementDelta`. How far a party's contracts settle
 * against the winning offer — we store the offer, DXMAP stores the signed
 * contract. Null below the API's sample floor; the tile renders nothing then.
 */
export interface SettlementDelta {
  sample: number;
  offerUzs: number;
  contractUzs: number;
  /** Signed: positive = below the offer, negative = ABOVE it. */
  discountPct: number;
  belowCount: number;
  belowPct: number;
}

export interface SupplierProfile {
  tin: string;
  name: string | null;
  sector: string | null;
  region: string | null;
  status: string | null;
  totalContracts: number;
  totalValueUzs: number;
  avgValueUzs: number | null;
  firstAwardAt: string | null;
  lastAwardAt: string | null;
  distinctBuyers: number;
  topBuyers: SupplierBuyerRef[];
  categoryMix: SupplierCategoryRef[];
  regionReach: SupplierRegionRef[];
  debarred: SupplierDebarredInfo | null;
  settlement: SettlementDelta | null;
  // Soliq trust facts (feed the «О поставщике» strip) + the composite badge —
  // present on the Pro payload, so the paid view carries the trust signal.
  vatPayer: boolean | null;
  charterFundUzs: string | null;
  statusGroup: string | null;
  regDate: string | null;
  liquidationDate: string | null;
  goodStanding: GoodStanding;
  /** DXMAP's state rating letter + the sector it was assigned in — see the
   *  public profile's fields. Optional for the one-deploy-behind window. */
  dxmapRateType?: string | null;
  dxmapSector?: string | null;
  /** КГД register hits (KZ only) — see `BuyerTrustStrip`. Optional for the
   *  one-deploy-behind window. */
  kgdFlags?: string[];
  /** The supplier's own phone/email — on this PAID payload only, absent when
   *  the registry holds neither. The public profile never carries it. */
  contact?: WinnerContact;
  /** Set (and `contact` omitted) when this account is over today's
   *  winner-contact ceiling (200 a Tashkent day, shared with the lot page). */
  contactCapped?: boolean;
}
export type SupplierProfileResponse =
  | { locked: true }
  | { locked: false; profile: SupplierProfile | null };

export async function getSupplierProfile(tin: string): Promise<SupplierProfileResponse> {
  return apiRequest<SupplierProfileResponse>(`/suppliers/${encodeURIComponent(tin)}/profile`);
}

// ── Buyer profile + favoritism (#48, Pro) ──────────────────────────────
export interface BuyerWinner {
  tin: string;
  name: string | null;
  wins: number;
  valueUzs: number;
  valueSharePct: number;
  countSharePct: number;
}
export interface BuyerProfile {
  tin: string;
  name: string | null;
  region: string | null;
  totalAwards: number;
  totalValueUzs: number;
  /** Awards whose winner was never identified — absent from `concentration`,
   *  so `totalAwards − unattributedAwards` is the count the concentration is
   *  computed from. Optional for the one-deploy-behind window. */
  unattributedAwards?: number;
  activeTenders: number;
  concentration: {
    hhi: number;
    distinctSuppliers: number;
    topSupplierShareByValuePct: number;
    topSupplierShareByCountPct: number;
    singleSupplierDependence: boolean;
  };
  topWinners: BuyerWinner[];
  topWinnerStreak: number;
  awardedByYear: { year: string; n: number }[];
  categoryMix: { section: string; name: string | null; awards: number; valueUzs: number }[];
  recentAwards: {
    tenderId: string | null;
    title: string | null;
    /** Optional: an API one deploy behind does not send it. */
    winnerTin?: string | null;
    winnerName: string | null;
    // No winner phone/email on the buyer page (owner decision 2026-10-07) —
    // contacts live on the lot outcome block and the supplier header only.
    valueUzs: number;
    awardedAt: string | null;
  }[];
  settlement: SettlementDelta | null;
  // Soliq trust facts (feed the «О заказчике» strip) + the composite badge —
  // present on the Pro payload too, so the paid view matches the public one.
  status: string | null;
  vatPayer: boolean | null;
  charterFundUzs: string | null;
  statusGroup: string | null;
  regDate: string | null;
  liquidationDate: string | null;
  goodStanding: GoodStanding;
  /** DXMAP's state rating letter + the sector it was assigned in — see the
   *  public profile's fields. Optional for the one-deploy-behind window. */
  dxmapRateType?: string | null;
  dxmapSector?: string | null;
  /** КГД register hits (KZ only) — see `BuyerTrustStrip`. Optional for the
   *  one-deploy-behind window. */
  kgdFlags?: string[];
}
export type BuyerProfileResponse =
  | { locked: true }
  | { locked: false; profile: BuyerProfile | null };

/**
 * Formerly the floor for the buyer page's concentration/favoritism block. Since
 * 2026-10-07 that block renders from 1 contract and only the verdict's strength
 * scales with the count (`lib/concentration-read.ts` — owner: «не надо скрывать,
 * просто не надо кричать»). What remains here is the legacy indexability
 * fallback in `buyer-profile.tsx` (for an API one deploy behind that sends no
 * `indexable`); the real rule is decided on the server
 * (`services/api/src/modules/catalog/company-index-rule.ts`).
 */
export const MIN_BUYER_AWARDS = 5;

/**
 * ⚠️ LEGACY indexability floor — a FALLBACK, not the rule.
 *
 * The rule now lives on the server and rides on `PublicSupplierProfile.indexable`.
 * This constant survives for exactly one window: `prerender-tenders` has only an
 * OPTIONAL need on `deploy-api`, so a web-only merge renders the whole corpus
 * against the PREVIOUS API, which sends no `indexable`. Without the fallback that
 * run would emit ~24k company pages with `noindex` — the failure this work exists
 * to remove, applied at scale for one cycle.
 */
export const MIN_SUPPLIER_AWARDS = 5;

/** Anonymous tier of a buyer page — aggregates only, never a named supplier.
 *  Public and server-seeded so the page has real content in the crawled DOM. */
/** Composite «good standing» tier — mirror of the API `GoodStanding`
 *  (`services/api/src/modules/catalog/good-standing.ts`). A transparent tier,
 *  not a score. `label` is the API's Russian label; the UI renders a localized
 *  label from the `tier` instead (see `GoodStandingBadge`). `unknown` → no badge. */
export type GoodStandingTier =
  | 'debarred'
  | 'liquidated'
  | 'flagged'
  | 'active_supplier'
  | 'active'
  | 'unknown';
export interface GoodStandingSignal {
  key: string;
  value: string | number | boolean;
}
export interface GoodStanding {
  tier: GoodStandingTier;
  label: string | null;
  signals: GoodStandingSignal[];
}

/**
 * The server's indexability verdict, mirrored from the API's
 * `CompanyIndexability` (`services/api/src/modules/catalog/public-profile.ts`).
 *
 * Both fields are OPTIONAL on purpose. They are absent from an API one deploy
 * behind, and the prerender can render against exactly that (see
 * `MIN_SUPPLIER_AWARDS`), so every reader must have a fallback rather than
 * treating `undefined` as `false` and shipping `noindex`.
 */
export interface CompanyIndexability {
  /** Is this page in `/buyers/index` / `/suppliers/index` and the sitemap? The
   *  single input to `noIndex` / `canonical` / JSON-LD on the page. */
  indexable?: boolean;
  /** Awards in the last 24 months — the recency arm of the supplier rule, and a
   *  fact the meta description quotes. */
  awards24m?: number;
}

export interface PublicBuyerProfile extends CompanyIndexability {
  tin: string;
  name: string | null;
  /** INN-first URL slug (`{tin}-{name}`), server-built from the registry name —
   *  the same value the sitemap advertises, so the page canonical matches. */
  slug: string;
  region: string | null;
  totalAwards: number;
  totalValueUzs: number;
  activeTenders: number;
  distinctSuppliers: number;
  /** No year series — see `public-profile.ts`: 90% of buyers peak in 2025,
   *  which is our harvest shape rather than their behaviour. */
  categoryMix: { section: string; name: string | null; awards: number; valueUzs: number }[];
  /** Soliq trust strip — the buyer's own registry facts. Public on purpose,
   *  same posture as the supplier `status`/`debarred` fields. No `regDate`
   *  here — that field exists only on the tender-detail `buyerTrust`.
   *  `charterFundUzs` is a string — BigInt has no native JSON representation.
   *  `liquidationDate` is ISO `yyyy-MM-dd`, or null if not liquidated. */
  status: string | null;
  vatPayer: boolean | null;
  charterFundUzs: string | null;
  statusGroup: string | null;
  liquidationDate: string | null;
  /** Composite «good standing» tier (public trust signal). */
  goodStanding: GoodStanding | null;
  /**
   * DXMAP's state-assigned rating — the LETTER only («Reyting» on
   * xarid.icppa.uz: AAA/AA/A · BBB/BB/B · CCC/CC/C · DDD/DD/D), plus the
   * sector it was assigned in, Uzbek Cyrillic, rendered verbatim.
   *
   * Optional for the one-deploy-behind window, and null whenever we hold no
   * rating for this ИНН — the strip then renders no rating row at all. The
   * numeric score is deliberately not in the payload: DXMAP publishes no band
   * thresholds, so the number cannot be interpreted. Wherever this renders,
   * the «xarid.icppa.uz» attribution DXMAP's licence requires renders with it.
   */
  dxmapRateType?: string | null;
  dxmapSector?: string | null;
  /** КГД register hits (KZ only) — see `BuyerTrustStrip`. Optional for the
   *  one-deploy-behind window. */
  kgdFlags?: string[];
  /** «Закупки за 12 месяцев» — counts, sums, medians from the award graph, each
   *  with its own coverage; see the API's BuyerProcurement12m. Optional for the
   *  one-deploy-behind window. */
  procurement12m?: {
    awards: number;
    valueUzs: number;
    pricedAwards: number;
    medianDiscountPct: number | null;
    countedAwards: number;
    medianParticipants: number | null;
    singleBidderPct: number | null;
  } | null;
  /** Recent tenders, winner redacted (the pairing is Pro). Optional: an API
   *  one deploy behind sends none and the section simply does not render. */
  recentTenders?: {
    tenderId: string | null;
    /** Slug of the linked lot, so the row links to the CANONICAL
     *  `/tender/{slug}-{id}` via `tenderPath` and not to the bare-id URL, which
     *  200s and then self-canonicalises. Optional for the one-deploy-behind
     *  window: absent → `tenderPath` falls back to the bare id, i.e. today's
     *  behaviour. */
    slug?: string | null;
    title: string | null;
    valueUzs: number;
    awardedAt: string | null;
  }[];
}

/** Anonymous tier of a supplier page. */
export interface PublicSupplierProfile extends CompanyIndexability {
  tin: string;
  name: string | null;
  sector: string | null;
  region: string | null;
  status: string | null;
  totalContracts: number;
  totalValueUzs: number;
  avgValueUzs: number | null;
  firstAwardAt: string | null;
  lastAwardAt: string | null;
  distinctBuyers: number;
  /** Newest contracts, buyer redacted (the pairing is Pro). Optional: an API
   *  one deploy behind sends none and the section does not render. */
  recentContracts?: {
    tenderId: string | null;
    /** Slug of the linked lot — see the buyer profile's field. */
    slug?: string | null;
    title: string | null;
    valueUzs: number;
    awardedAt: string | null;
  }[];
  /** «Госзакупки за 12 месяцев» — see the API's SupplierProcurement12m for what
   *  each figure can claim. Optional for the one-deploy-behind window. */
  procurement12m?: {
    wins: number;
    valueUzs: number;
    pricedAwards: number;
    medianDiscountPct: number | null;
    participations: number;
    winsWithDetail: number;
    medianParticipants: number | null;
  } | null;
  categoryMix: { section: string; name: string | null; contracts: number; valueUzs: number }[];
  regionReach: { region: string | null; contracts: number; code?: string | null }[];
  debarred: { reason: string | null; until: string | null } | null;
  /** Composite «good standing» tier (public trust signal). */
  goodStanding: GoodStanding | null;
  /** Soliq trust facts for the public «О поставщике» strip. */
  vatPayer: boolean | null;
  charterFundUzs: string | null;
  statusGroup: string | null;
  regDate: string | null;
  liquidationDate: string | null;
  /**
   * DXMAP's state-assigned rating — the LETTER only («Reyting» on
   * xarid.icppa.uz: AAA/AA/A · BBB/BB/B · CCC/CC/C · DDD/DD/D), plus the
   * sector it was assigned in, Uzbek Cyrillic, rendered verbatim.
   *
   * Optional for the one-deploy-behind window, and null whenever we hold no
   * rating for this ИНН — the strip then renders no rating row at all. The
   * numeric score is deliberately not in the payload: DXMAP publishes no band
   * thresholds, so the number cannot be interpreted. Wherever this renders,
   * the «xarid.icppa.uz» attribution DXMAP's licence requires renders with it.
   */
  dxmapRateType?: string | null;
  dxmapSector?: string | null;
  /** КГД register hits (KZ only) — see `BuyerTrustStrip`. Optional for the
   *  one-deploy-behind window. */
  kgdFlags?: string[];
}

// ─── Company directory (#Good Standing) ───────────────────────────────────────

export interface CompanyListRow {
  tin: string;
  name: string | null;
  sector: string | null;
  region: string | null;
  role: 'supplier' | 'buyer' | 'both';
  registryStatus: string | null;
  goodStanding: GoodStanding | null;
  /** Which capacity `totalContracts`/`totalValueUzs`/`goodStanding` describe —
   *  the role filter's, or the larger direction without one. Optional for the
   *  one-deploy-behind window (an older API sends neither this nor the split). */
  countsAs?: 'supplier' | 'buyer';
  totalContracts: number;
  totalValueUzs: number;
  supplierContracts?: number;
  buyerContracts?: number;
}
export interface CompanyListResult {
  items: CompanyListRow[];
  total: number;
  offset: number;
  limit: number;
}
export interface CompanyFacet {
  key: string;
  name: string;
  count: number;
}
export interface CompanyFilters {
  sector?: string;
  region?: string;
  status?: 'active' | 'liquidated';
  role?: 'supplier' | 'buyer' | 'both';
  q?: string;
  offset?: number;
  limit?: number;
}

export async function fetchCompanies(filters: CompanyFilters = {}): Promise<CompanyListResult> {
  const p = new URLSearchParams();
  if (filters.sector) p.set('sector', filters.sector);
  if (filters.region) p.set('region', filters.region);
  if (filters.status) p.set('status', filters.status);
  if (filters.role) p.set('role', filters.role);
  if (filters.q) p.set('q', filters.q);
  if (filters.offset != null) p.set('offset', String(filters.offset));
  if (filters.limit != null) p.set('limit', String(filters.limit));
  const qs = p.toString();
  // The list endpoint uses the {data: rows[], meta: {pagination}} envelope
  // (same as /catalog) — apiRaw keeps the envelope so we can read the total.
  const env = await apiRaw<{
    data: CompanyListRow[];
    meta?: { pagination?: { total?: number; offset?: number; limit?: number } };
  }>(`/catalog/companies${qs ? `?${qs}` : ''}`);
  return {
    items: env.data ?? [],
    total: env.meta?.pagination?.total ?? 0,
    offset: env.meta?.pagination?.offset ?? 0,
    limit: env.meta?.pagination?.limit ?? 30,
  };
}
export async function fetchCompanySectors(): Promise<CompanyFacet[]> {
  return apiRequest<CompanyFacet[]>('/catalog/companies/sectors');
}
export async function fetchCompanyRegions(): Promise<CompanyFacet[]> {
  return apiRequest<CompanyFacet[]>('/catalog/companies/regions');
}

export async function getPublicBuyerProfile(
  tin: string,
  lang: string,
): Promise<{ profile: PublicBuyerProfile | null }> {
  return apiRequest<{ profile: PublicBuyerProfile | null }>(
    `/buyers/${encodeURIComponent(tin)}/public-profile?lang=${lang}`,
  );
}

/** One row of a category hub's "top buyers" strip (GET /buyers/by-category/:slug). */
export interface CategoryTopBuyer {
  tin: string;
  /** INN-first URL slug — link straight to the buyer page's canonical URL. */
  slug: string;
  name: string;
  tenderCount: number;
}

/** Top заказчики in a catalog category, for the /category/<slug> hub strip. */
export async function fetchTopBuyersByCategory(slug: string): Promise<CategoryTopBuyer[]> {
  return apiRequest<CategoryTopBuyer[]>(`/buyers/by-category/${encodeURIComponent(slug)}`);
}

export async function getPublicSupplierProfile(
  tin: string,
): Promise<{ profile: PublicSupplierProfile | null }> {
  return apiRequest<{ profile: PublicSupplierProfile | null }>(
    `/suppliers/${encodeURIComponent(tin)}/public-profile`,
  );
}

/** One row of the Pro contracts list on `/supplier/:tin` — with its buyer.
 *  Pro-only on purpose: see the API's `SupplierContractRowJson`. */
export interface SupplierContractListRow {
  tenderId: string | null;
  slug?: string | null;
  title: string | null;
  valueUzs: number;
  awardedAt: string | null;
  buyerTin: string | null;
  buyerName: string | null;
}
export type SupplierContractsResponse =
  | { locked: true }
  | {
      locked: false;
      rows: SupplierContractListRow[];
      /** Offset of the next page, or null at the end of the list. */
      nextOffset: number | null;
    };

/** One page of a supplier's contracts, newest first (Pro; anonymous → 401). */
export async function getSupplierContracts(
  tin: string,
  offset: number,
): Promise<SupplierContractsResponse> {
  return apiRequest<SupplierContractsResponse>(
    `/suppliers/${encodeURIComponent(tin)}/contracts?offset=${offset}`,
  );
}

export async function getBuyerProfile(tin: string, lang: string): Promise<BuyerProfileResponse> {
  return apiRequest<BuyerProfileResponse>(
    `/buyers/${encodeURIComponent(tin)}/profile?lang=${lang}`,
  );
}

// ─── Price reference (#46) ────────────────────────────────────────────────────

export interface PriceProduct {
  ikpu: string;
  name: string;
  count: number;
  /** Neutral URL slug for `/prices/{slug}-{ikpu}` (#46). */
  slug: string;
}
export interface PriceStat {
  p25: number;
  median: number;
  p75: number;
  n: number;
}
export interface PriceMetricStat {
  median: number;
  n: number;
  /** Share of the sample on the single most common value — see the API's
   *  MetricStat. Above ~60% the median is a platform rule, not a market rate. */
  modeSharePct: number;
}
export interface PriceReference {
  ikpu: string;
  sample: number;
  dateRange: { from: string; to: string } | null;
  winningPriceUzs: PriceStat | null;
  discountPct: PriceMetricStat | null;
  participants: PriceMetricStat | null;
}
/** Direct contracts for the product (no tender) — the API keeps them OUT of
 *  `stats` and sends them as this separate figure; null below 5. */
export interface PriceDirectContracts {
  count: number;
  medianUzs: number;
}
export type PriceReferenceResponse =
  | { locked: true; hasData: boolean }
  | {
      locked: false;
      stats: PriceReference | null;
      hasData: boolean;
      name: string;
      slug: string;
      /** Optional: an API from before 2026-10-17 does not send it. */
      direct?: PriceDirectContracts | null;
    };

export async function fetchPriceProducts(lang?: string): Promise<PriceProduct[]> {
  return apiRequest<PriceProduct[]>(`/prices${lang ? `?lang=${lang}` : ''}`);
}
export async function getPriceReference(
  ikpu: string,
  lang?: string,
): Promise<PriceReferenceResponse> {
  return apiRequest<PriceReferenceResponse>(
    `/prices/${encodeURIComponent(ikpu)}${lang ? `?lang=${lang}` : ''}`,
  );
}

// ─── IFI projects by ministry/agency (#52) ────────────────────────────────────

export interface IfiProject {
  id: string;
  slug: string | null;
  title: string;
  projectName: string | null;
}
export interface IfiAgencyProjects {
  agency: string;
  projects: IfiProject[];
}

/** Public — the current IFI tenders grouped by their executing ministry/agency.
 *  `ifiSource` narrows the breakdown to the institution the chip strip selected,
 *  so the panel agrees with the list above it instead of always showing the
 *  whole layer. */
export async function fetchIfiProjects(ifiSource?: IfiSourceKey): Promise<IfiAgencyProjects[]> {
  const qs = ifiSource ? `?ifiSource=${encodeURIComponent(ifiSource)}` : '';
  return apiRequest<IfiAgencyProjects[]>(`/catalog/ifi/projects${qs}`);
}
